import React, { useEffect, useState, useRef, useMemo } from "react";
import { toast } from "sonner";
import { api, fileUrl, fileToBase64 } from "@/lib/api";
import { useSession } from "@/lib/session";
import { SUPPLIERS, CATEGORIES, CATEGORY_DEFAULT_MARGIN, waLink } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  Camera,
  Phone,
  ReceiptText,
  Calculator,
  Plus,
  Trash2,
  Pencil,
  TrendingUp,
  Tag,
  Download,
  FileText,
  AlertTriangle,
  Check,
  ScanLine,
  Sparkles,
  Printer,
  Barcode,
  TrendingDown,
  RefreshCw,
  Layers,
  ShieldCheck,
} from "lucide-react";

import BarcodeScannerDialog from "@/components/BarcodeScanner";
import CreditNoteDialog from "@/components/CreditNoteDialog";
import { useT } from "@/lib/i18n";

const STATUS_META = {
  PAID_CASH: { label: "PAID · CASH", className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  PAID_VISA: { label: "PAID · VISA", className: "bg-blue-100 text-blue-700 border-blue-200" },
  UNPAID: { label: "UNPAID", className: "bg-slate-200 text-slate-700 border-slate-300" },
  PENDING_CHEQUE: { label: "PENDING · CHEQUE", className: "bg-yellow-200 text-yellow-900 border-yellow-300" },
};

const formatPaymentLine = (inv) => {
  const supplier = inv.supplier_name || "Unknown supplier";
  if (inv.status === "PAID_CASH") return `${supplier} | PAID-CASH | €${Number(inv.total_amount || 0).toFixed(2)}`;
  if (inv.status === "PAID_VISA") {
    const ref = inv.payment_ref ? ` (Ref: ${inv.payment_ref})` : "";
    return `${supplier} | PAID-VISA${ref} | €${Number(inv.total_amount || 0).toFixed(2)}`;
  }
  if (inv.status === "PENDING_CHEQUE") {
    const n = inv.cheque_number || "—";
    return `${supplier} | PENDING-CHEQUE #${n} | €${Number(inv.total_amount || 0).toFixed(2)}`;
  }
  return `${supplier} | UNPAID | €${Number(inv.total_amount || 0).toFixed(2)}`;
};

const VAT_RATES = [0, 5, 19];

export default function Invoices() {
  const t = useT();
  const { shift, setPendingDraft, voucherPreview, setVoucherPreview, portalUnlocked, liveViewUnlocked } = useSession();
  const ownerActive = portalUnlocked || liveViewUnlocked;
  const [invoices, setInvoices] = useState([]);
  const [cashflow, setCashflow] = useState([]);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(null);
  const [editForm, setEditForm] = useState({});
  // NEW (iter10): preview pipeline — extracted OCR data shown to the user but
  // NOT yet committed to the shift. Locks in only when "+ ADD TO SHIFT" is clicked.
  const [preview, setPreview] = useState(null);
  const [previewForm, setPreviewForm] = useState({});
  const [committing, setCommitting] = useState(false);
  // Cashflow voucher (handed off from the AI Camera on the Cashflow tab).
  const [voucherForm, setVoucherForm] = useState({});
  const [voucherCommitting, setVoucherCommitting] = useState(false);
  // Iter 23 — Shift-validation modal. Intercepts the green "+ ADD TO SHIFT"
  // click on BOTH the OCR Invoice Preview and the Cashflow Voucher Preview.
  // The actual commit only fires after the operator picks "Yes, Add Now".
  // "No, later" keeps the preview dialog open so the row stays as a draft
  // and the End Shift golden rule continues to block SAVE & CLOSE.
  const [validationOpen, setValidationOpen] = useState(false);
  const [pendingCommit, setPendingCommit] = useState(null); // { run, summary }
  // Iter 24 — When the OCR preview indicates a cheque (ocr_mode ===
  // "cheque_invoice", status PENDING_CHEQUE, or cheque_number present)
  // the modal switches to the dedicated Cheque-Bypass copy explaining
  // the 0.00 drawer effect + auto-route to the Master Cheque Calendar.
  const [validationMode, setValidationMode] = useState("invoice"); // "invoice" | "cheque"
  // Iter 25 — AI Match modal (Cheque → Credit-Invoice cross-reference).
  // When a cheque is committed AND `/api/invoices/match-cheque` returns
  // ≥1 hit, this dialog opens FIRST. The operator picks:
  //   • "Yes, Link & Close" → stores linkInvoiceId, then chains to the
  //     existing Iter-24 Cheque-Detected modal for final confirmation.
  //   • "No, New Entry"     → skips linking, chains to Cheque modal.
  const [aiMatchOpen, setAiMatchOpen] = useState(false);
  const [aiMatches, setAiMatches] = useState([]);
  const [linkInvoiceId, setLinkInvoiceId] = useState(null);
  // Iter 91 — Split-Screen AI-OCR Cheque Verification Modal. Auto-pops the
  // moment /api/ocr/invoice returns ocr_mode='cheque_invoice'. Left panel
  // renders the scanned cheque image with zoom controls; right panel shows
  // editable Amount/Maturity/Cheque-Number/Bank fields pre-filled by the
  // Gemini/GPT-5.2 vision pass. On confirm the existing _runCommitPreview
  // pipeline fires and the cheque maturity auto-pins onto the Master Cheque
  // Calendar via commit_invoice's db.cheques insert.
  const [chequeVerifyOpen, setChequeVerifyOpen] = useState(false);
  const [chequeZoom, setChequeZoom] = useState(1);
  const camRef = useRef(null);

  // Broadcast the draft state globally so TopNav can gate CASHFLOW / END SHIFT.
  // Empty shifts (no draft on screen) navigate freely.
  useEffect(() => {
    setPendingDraft(!!preview || !!voucherPreview);
    return () => setPendingDraft(false);
  }, [preview, voucherPreview, setPendingDraft]);

  // Mirror the inbound voucherPreview into editable form state.
  useEffect(() => {
    if (voucherPreview) setVoucherForm(voucherPreview);
  }, [voucherPreview]);

  const location = shift?.location;

  const load = async () => {
    if (!shift) {
      setInvoices([]);
      setCashflow([]);
      return;
    }
    try {
      // Strict Session_ID isolation — only show invoices linked to THIS
      // register's active shift. Register 1 & 2 at the same kiosk never
      // see each other's working set.
      const [inv, cf] = await Promise.all([
        api.get("/invoices", { params: { shift_id: shift.id } }),
        api.get("/cashflow", { params: { shift_id: shift.id } }),
      ]);
      setInvoices(inv.data);
      setCashflow(cf.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, [shift?.id]);

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice "${inv.supplier_name || inv.invoice_number || "—"}" (€${inv.total_amount.toFixed(2)})?\nThe Owner will see this in the audit log.`)) return;
    try {
      const { data } = await api.delete(`/invoices/${inv.id}`);
      toast.success(`Reversed €${data.reversed_amount.toFixed(2)} — Expected Cash & OpEx updated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const deleteCashflow = async (ev) => {
    const label = ev.note || ev.kind;
    if (!window.confirm(`Delete cashflow entry "${label}" (€${Number(ev.amount).toFixed(2)})?\nThe Owner will see this in the audit log.`)) return;
    try {
      const { data } = await api.delete(`/cashflow/${ev.id}`);
      toast.success(`Reversed €${data.reversed_amount.toFixed(2)} — Expected Cash & OpEx updated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!shift) {
      toast.error("Start a shift first (Tab 1)");
      return;
    }
    setOcrBusy(true);
    const t = toast.loading("Scanning document with AI…");
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/ocr/invoice", {
        image_base64: b64,
        shift_id: shift.id,
        location: shift.location,
      });
      // mode label for toast
      const modeLabel =
        data.ocr_mode === "cheque_invoice"
          ? "Invoice + Cheque"
          : data.ocr_mode === "paid_receipt"
          ? `Paid Receipt (${data.payment_method || "?"})`
          : "Invoice";
      toast.success(`AI detected: ${modeLabel}`, { id: t });
      setPreview(data);
      setPreviewForm(data);
      // Iter 91 — Auto-pop the Split-Screen Cheque Verification modal when
      // the AI detects a cheque scan. The existing OCR preview dialog
      // (`ocr-preview-dialog`) stays mounted underneath as the secondary
      // confirmation step; the split-screen view sits ON TOP because it
      // gives the operator the side-by-side image-vs-fields layout that
      // Bank-of-Cyprus / Hellenic-Bank cheque verification requires.
      if (data?.ocr_mode === "cheque_invoice") {
        setChequeZoom(1);
        setChequeVerifyOpen(true);
      }
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "OCR failed", { id: t });
    } finally {
      setOcrBusy(false);
    }
  };

  // Iter 23 — Internal commit (the actual POST). Renamed from
  // `commitPreview` so the public `commitPreview` (called by the green
  // button) can now open the shift-validation modal first.
  const _runCommitPreview = async () => {
    if (!preview) return;
    setCommitting(true);
    const t = toast.loading("Adding to shift…");
    try {
      const payload = {
        shift_id: shift?.id || null,
        location: shift?.location || preview.location,
        ocr_mode: preview.ocr_mode || "invoice_only",
        supplier_name: previewForm.supplier_name || "",
        invoice_number: previewForm.invoice_number || "",
        issue_date: previewForm.issue_date || "",
        net_amount: Number(previewForm.net_amount) || 0,
        vat_rate: Number(previewForm.vat_rate) || 0,
        vat_amount: Number(previewForm.vat_amount) || 0,
        total_amount: Number(previewForm.total_amount) || 0,
        status: previewForm.status || "UNPAID",
        payment_method: previewForm.payment_method || null,
        payment_ref: previewForm.payment_ref || null,
        cheque_number: previewForm.cheque_number || null,
        cheque_amount: previewForm.cheque_amount ? Number(previewForm.cheque_amount) : null,
        cheque_maturity: previewForm.cheque_maturity || null,
        cheque_bank: previewForm.cheque_bank || preview.cheque_bank || null,
        invoice_image_url: preview.invoice_image_url || null,
        invoice_image_path: preview.invoice_image_path || null,
        cheque_image_url: preview.cheque_image_url || null,
        cheque_image_path: preview.cheque_image_path || null,
        raw_ocr: preview.raw_ocr || null,
      };
      await api.post("/invoices/commit", payload);
      toast.success("Locked into shift", { id: t });
      setPreview(null);
      setPreviewForm({});
      // Iter 26 — Await the refresh so React commits the new state into
      // BOTH the Active Shift Items list and the Recent Invoices panel
      // before the modal closes. Without the await, fast users saw a
      // stale "Nothing added to this shift yet" placeholder for a beat.
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to add to shift", { id: t });
    } finally {
      setCommitting(false);
    }
  };

  // ── Shift-validation modal interceptor (Iter 23) ────────────────────
  // Public entrypoints called by the green "+ ADD TO SHIFT" buttons.
  // They open the validation modal instead of committing directly.
  // "Yes, Add Now" → pendingCommit.run() executes the internal commit.
  // "No, later"    → modal closes, preview/voucher dialog stays open,
  //                  draft remains active so the End Shift golden rule
  //                  continues to block SAVE & CLOSE.
  const commitPreview = async () => {
    if (!preview) return;
    const amount = Number(previewForm.total_amount || preview.total_amount || 0);
    const isCheque =
      preview?.ocr_mode === "cheque_invoice" ||
      previewForm?.status === "PENDING_CHEQUE" ||
      !!(previewForm?.cheque_number && String(previewForm.cheque_number).trim());

    // Iter 79 — When the operator marks the invoice as PAID · VISA and
    // the JCC / Viva Wallet integration is active, push the EXACT amount
    // to the card terminal so the cashier cannot key in a different
    // value (anti-double-entry). Fire-and-forget so a gateway hiccup
    // never blocks the existing OCR / cheque match flow.
    const isVisaPay =
      previewForm?.status === "PAID_VISA" || previewForm?.payment_method === "VISA";
    if (isVisaPay && amount > 0) {
      api.post("/jcc-viva/push-amount", {
        amount,
        ref: previewForm?.invoice_number || preview?.invoice_number || "",
      }).then(({ data: d }) => {
        if (d?.ok) {
          toast.success(`💳 €${Number(d.amount).toFixed(2)} pushed to terminal`);
        }
      }).catch(() => { /* integration off or transient — silent */ });
    }

    // Iter 25 — Cheque path: search the ledger for matching credit invoices
    // (same supplier, same amount, status UNPAID|CREDIT) and offer the
    // AI Match dialog so the operator can close the outstanding balance
    // in one tap.
    if (isCheque) {
      try {
        const { data } = await api.post("/invoices/match-cheque", {
          supplier_name: previewForm.supplier_name || preview.supplier_name || "",
          total_amount: amount,
        });
        if (Array.isArray(data?.matches) && data.matches.length > 0) {
          setAiMatches(data.matches);
          setAiMatchOpen(true);
          return; // wait for the operator's choice; chains to cheque modal next
        }
      } catch { /* lookup is best-effort — fall through to cheque modal */ }
    }

    openShiftValidation(isCheque);
  };

  // Helper — opens the standard SHIFT VALIDATION / CHEQUE DETECTED modal.
  // Extracted so the AI Match flow can chain into it after the operator
  // picks "Yes, Link & Close" or "No, New Entry".
  const openShiftValidation = (isCheque) => {
    if (!preview) return;
    const amount = Number(previewForm.total_amount || preview.total_amount || 0);
    setValidationMode(isCheque ? "cheque" : "invoice");
    setPendingCommit({
      run: _runCommitPreview,
      summary: {
        kind: isCheque ? "CHEQUE" : "INVOICE",
        supplier: previewForm.supplier_name || preview.supplier_name || "—",
        amount,
        status: isCheque
          ? `Cheque #${previewForm.cheque_number || preview.cheque_number || "—"}`
          : (previewForm.status || preview.status || "UNPAID"),
        maturity: isCheque
          ? (previewForm.cheque_maturity || preview.cheque_maturity || "—")
          : null,
      },
    });
    setValidationOpen(true);
  };

  const commitVoucher = () => {
    if (!voucherPreview || !shift) return;
    const amount = Number(voucherForm.amount || 0);
    setValidationMode("invoice");
    setPendingCommit({
      run: _runCommitVoucher,
      summary: {
        kind: voucherForm.kind === "CASH_DROP" ? "CASH DROP" : "PAYOUT",
        supplier: voucherForm.employee_name || voucherForm.category || voucherForm.note || "Cashflow voucher",
        amount,
        status: voucherForm.payment_source || "DRAWER",
      },
    });
    setValidationOpen(true);
  };

  const acceptValidationAndCommit = async () => {
    const fn = pendingCommit?.run;
    const linkId = linkInvoiceId; // capture before we clear state
    const chequeNum = previewForm?.cheque_number || preview?.cheque_number || null;
    const chequeMat = previewForm?.cheque_maturity || preview?.cheque_maturity || null;
    setValidationOpen(false);
    setPendingCommit(null);
    if (typeof fn === "function") {
      await fn();
    }
    // Iter 25 — After the cheque commits, mark the linked credit invoice
    // as PAID_WITH_CHEQUE so the audit chain closes cleanly. Best-effort
    // — failure here doesn't roll back the cheque commit.
    if (linkId) {
      try {
        await api.post(`/invoices/${linkId}/link-cheque`, {
          cheque_number: chequeNum,
          cheque_maturity: chequeMat,
        });
        toast.success("Credit invoice marked PAID_WITH_CHEQUE · Πληρώθηκε με Επιταγή");
      } catch (e) {
        toast.error(`Link failed: ${e?.message || e}`);
      } finally {
        setLinkInvoiceId(null);
      }
    }
  };

  const dismissValidationAsDraft = () => {
    setValidationOpen(false);
    setPendingCommit(null);
    setLinkInvoiceId(null);
    toast.message("Kept as draft · Επιστροφή αργότερα", {
      description: "End Shift will still block closure until this is added.",
    });
  };

  const discardPreview = () => {
    setPreview(null);
    setPreviewForm({});
    toast("Discarded — nothing was saved", { icon: "🗑️" });
  };

  // --- Cashflow voucher commit / discard --------------------------------
  // Iter 23 — Internal commit. Public `commitVoucher` now opens the
  // validation modal first.
  const _runCommitVoucher = async () => {
    if (!voucherPreview || !shift) return;
    const amt = voucherForm.amount === "" || voucherForm.amount == null
      ? 0.0
      : Number(voucherForm.amount);
    if (!amt || amt <= 0) {
      toast.error("Amount is missing or invalid — please correct before adding");
      return;
    }
    const kind = voucherForm.kind || "PAYOUT";
    // Category is only mandatory when the entry is a staff payment / advance
    // OR an OCR voucher (where category drives the OpEx payroll handshake).
    // Manual cash-out / cash-drop entries don't need a category.
    const needsCategory = voucherForm._origin === "staff" || voucherForm._origin === "voucher";
    if (needsCategory && !voucherForm.category) {
      toast.error("Pick a category before adding to shift");
      return;
    }
    setVoucherCommitting(true);
    const t = toast.loading("Adding to shift…");
    try {
      // Server-side single transaction:
      //   (1) db.cashflow row (kind, amount, note, category, employee_name)
      //   (2) Expected Cash auto-subtracts (existing /shifts/{id}/expected math)
      //   (3) OpEx payroll row when category is Salary Payment / Salary Advance
      //   (4) Starting Float (€700) untouched
      const payload = {
        shift_id: shift.id,
        location: shift.location,
        kind,
        amount: amt,
        payment_source: voucherForm.payment_source || "DRAWER",
        category: voucherForm.category || null,
        employee_name: voucherForm.employee_name || "",
        cheque_number:
          (voucherForm.payment_source || "DRAWER") === "CHEQUE"
            ? (voucherForm.cheque_number || "").trim() || null
            : null,
        cheque_maturity:
          (voucherForm.payment_source || "DRAWER") === "CHEQUE"
            ? voucherForm.cheque_maturity || null
            : null,
        cheque_supplier:
          (voucherForm.payment_source || "DRAWER") === "CHEQUE"
            ? (voucherForm.cheque_supplier || "").trim() || null
            : null,
        transaction_date: voucherForm.transaction_date || null,
        note:
          voucherForm.note ||
          (voucherForm.category ? `Voucher · ${voucherForm.category}` : ""),
      };
      if (voucherForm.image_base64) payload.image_base64 = voucherForm.image_base64;
      await api.post("/cashflow", payload);
      const label = voucherForm.category
        ? voucherForm.category
        : kind === "PAYOUT" ? "Payout" : "Cash Drop";
      toast.success(`${label} · €${amt.toFixed(2)} added to shift`, { id: t });
      setVoucherPreview(null);
      setVoucherForm({});
      // Iter 26 — Await refresh so Cashflow rows surface immediately
      // in the Active Shift Items & Recent Invoices lists.
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to add to shift", { id: t });
    } finally {
      setVoucherCommitting(false);
    }
  };

  const discardVoucher = () => {
    setVoucherPreview(null);
    setVoucherForm({});
    toast("Voucher discarded — nothing was saved", { icon: "🗑️" });
  };

  const saveEdit = async (extras = {}) => {
    try {
      const { data } = await api.patch(`/invoices/${openInvoice.id}`, {
        supplier_name: editForm.supplier_name,
        invoice_number: editForm.invoice_number,
        issue_date: editForm.issue_date,
        net_amount: Number(editForm.net_amount),
        vat_rate: Number(editForm.vat_rate),
        vat_amount: Number(editForm.vat_amount),
        total_amount: Number(editForm.total_amount),
        status: editForm.status,
        payment_method: editForm.payment_method || null,
        payment_ref: editForm.payment_ref || null,
        cheque_number: editForm.cheque_number || null,
        cheque_maturity: editForm.cheque_maturity || null,
        // Iter 73 — interactive status/source modal extras (status
        // override OR mark_paid_source for CASH/BANK mirror).
        ...extras,
      });
      setOpenInvoice(data);
      if (extras.mark_paid_source === "CASH") {
        toast.success("💵 Marked PAID · expense routed to active CASHFLOW shift");
      } else if (extras.mark_paid_source === "BANK") {
        toast.success("🏦 Marked PAID · logged as BANK transfer (drawer untouched)");
      } else if (extras.status) {
        toast.success(`Status → ${extras.status}`);
      } else {
        toast.success("Invoice updated");
      }
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="pb-32">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            {t("invoices.title")}
          </h1>
          <p className="text-slate-500 mt-1 tracking-wide">
            {t("invoices.subtitle")}
          </p>
        </div>
        {shift && (
          <div
            className="font-mono text-sm text-slate-600 bg-white border border-slate-200 px-4 py-2 rounded-lg"
            data-testid="invoices-shift-label"
          >
            {shift.location} · Register {shift.register} · {shift.employee_name}
          </div>
        )}
      </header>

      {/* 60 / 40 split */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT 60% — suppliers + recent OCR invoices */}
        <div className="lg:col-span-3 space-y-6">
          <ActiveShiftItems
            invoices={invoices}
            cashflow={cashflow}
            onDeleteInvoice={deleteInvoice}
            onDeleteCashflow={deleteCashflow}
            onEditInvoice={(i) => { setOpenInvoice(i); setEditForm(i); }}
          />
          <RecentInvoices invoices={invoices} onOpen={(i) => { setOpenInvoice(i); setEditForm(i); }} />
          <DailyStoreFolders onOpenInvoice={(i) => { setOpenInvoice(i); setEditForm(i); }} onInvoiceDeleted={load} />
          <SuppliersGrid />
        </div>

        {/* RIGHT 40% — Pricing Calculator */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 space-y-4">
            <PricingCalculator shift={shift} />
          </div>
        </div>
      </div>

      {/* Bottom-left FAB Camera */}
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCapture}
        className="hidden"
        data-testid="fab-camera-input"
      />
      <button
        onClick={() => camRef.current?.click()}
        disabled={ocrBusy}
        data-testid="fab-camera"
        className="fixed bottom-8 left-8 z-40 h-24 w-24 rounded-full bg-slate-900 text-white shadow-2xl hover:bg-slate-800 hover:scale-105 transition-all flex flex-col items-center justify-center disabled:opacity-60"
        title="AI OCR Document Scanner"
      >
        <Camera className="h-9 w-9" />
        <span className="text-[10px] font-bold mt-1 tracking-widest">{ocrBusy ? "SCAN…" : "AI · OCR"}</span>
      </button>

      {/* Invoice edit dialog */}
      <Dialog open={!!openInvoice} onOpenChange={(o) => !o && setOpenInvoice(null)}>
        <DialogContent className="max-w-3xl" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>Invoice Details · Λεπτομέρειες Τιμολογίου</DialogTitle>
          </DialogHeader>
          {openInvoice && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {openInvoice.invoice_image_url && (
                <img
                  src={fileUrl(openInvoice.invoice_image_url)}
                  alt="scan"
                  className="w-full rounded-lg border border-slate-200"
                  data-testid="invoice-dialog-image"
                />
              )}
              <div className="space-y-3">
                <Field label="Supplier" v={editForm.supplier_name} onChange={(v) => setEditForm({ ...editForm, supplier_name: v })} testId="edit-supplier" />
                <Field label="Invoice No." v={editForm.invoice_number} onChange={(v) => setEditForm({ ...editForm, invoice_number: v })} testId="edit-invno" />
                <Field label="Issue Date" v={editForm.issue_date} onChange={(v) => setEditForm({ ...editForm, issue_date: v })} testId="edit-issuedate" placeholder="YYYY-MM-DD" />
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Net €" v={editForm.net_amount} onChange={(v) => setEditForm({ ...editForm, net_amount: v })} testId="edit-net" type="number" />
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">VAT %</Label>
                    <Select value={String(editForm.vat_rate ?? 0)} onValueChange={(v) => setEditForm({ ...editForm, vat_rate: Number(v) })}>
                      <SelectTrigger className="mt-1" data-testid="edit-vatrate"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="19">19%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Field label="Total €" v={editForm.total_amount} onChange={(v) => setEditForm({ ...editForm, total_amount: v })} testId="edit-total" type="number" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Payment Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger className="mt-1" data-testid="edit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAID_CASH">PAID · CASH</SelectItem>
                      <SelectItem value="PAID_VISA">PAID · VISA</SelectItem>
                      <SelectItem value="UNPAID">UNPAID</SelectItem>
                      <SelectItem value="PENDING_CHEQUE">PENDING · CHEQUE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Cheque No." v={editForm.cheque_number || ""} onChange={(v) => setEditForm({ ...editForm, cheque_number: v })} testId="edit-chqno" />
                  <Field label="Cheque Maturity" v={editForm.cheque_maturity || ""} onChange={(v) => setEditForm({ ...editForm, cheque_maturity: v })} testId="edit-chqmat" placeholder="YYYY-MM-DD" />
                </div>
                {(editForm.status === "PAID_VISA" || editForm.payment_method === "VISA") && (
                  <Field
                    label="VISA Approval / Transaction No. · Νούμερο Έγκρισης"
                    v={editForm.payment_ref || ""}
                    onChange={(v) => setEditForm({ ...editForm, payment_ref: v })}
                    testId="edit-payment-ref"
                  />
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            {/* Iter 73 · Interactive Status & Payment-Source bar.
                Three status buttons + two source buttons. CASH writes
                to active cashflow opex; BANK logs as external payment
                with zero impact on counter-drawer math. */}
            <div className="w-full" data-testid="invoice-status-bar">
              <div className="grid grid-cols-3 gap-2 mb-2">
                <Button
                  onClick={() => saveEdit({ status: "PAID_CASH" })}
                  data-testid="invoice-status-paid"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                >🟢 Paid · Πληρώθηκε</Button>
                <Button
                  onClick={() => saveEdit({ status: "UNPAID" })}
                  data-testid="invoice-status-unpaid"
                  className="bg-red-600 hover:bg-red-700 text-white text-sm"
                >🔴 Unpaid · Απλήρωτο</Button>
                <Button
                  onClick={() => saveEdit({ status: "PENDING" })}
                  data-testid="invoice-status-pending"
                  className="bg-amber-500 hover:bg-amber-600 text-white text-sm"
                >🟡 Pending · Εκκρεμεί</Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => saveEdit({ mark_paid_source: "CASH" })}
                  data-testid="invoice-source-cash"
                  className="bg-amber-100 hover:bg-amber-200 border-2 border-amber-400 text-amber-900 font-bold"
                >💵 ΑΠΟ ΤΟ ΤΑΜΕΙΟ · CASH</Button>
                <Button
                  onClick={() => saveEdit({ mark_paid_source: "BANK" })}
                  data-testid="invoice-source-bank"
                  className="bg-sky-100 hover:bg-sky-200 border-2 border-sky-400 text-sky-900 font-bold"
                >🏦 EXTERNAL · BANK</Button>
              </div>
              <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-slate-200">
                <Button variant="outline" onClick={() => setOpenInvoice(null)} data-testid="invoice-dialog-close">Close</Button>
                <Button onClick={() => saveEdit()} data-testid="invoice-dialog-save">Save Changes</Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          AI OCR PREVIEW DIALOG (iter10) — extracted data is shown but NOT
          persisted to the shift until the user taps "+ ADD TO SHIFT".
      ------------------------------------------------------------------ */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && !committing && discardPreview()}>
        <DialogContent className="max-w-3xl" data-testid="ocr-preview-dialog">
          <DialogHeader>
            <DialogTitle>
              AI OCR Preview · Προεπισκόπηση
              {preview?.ocr_mode === "cheque_invoice" && " · Invoice + Cheque"}
              {preview?.ocr_mode === "paid_receipt" && ` · Paid Receipt (${preview?.payment_method || "?"})`}
              {preview?.ocr_mode === "invoice_only" && " · Invoice"}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {preview.invoice_image_url && (
                <img
                  src={fileUrl(preview.invoice_image_url)}
                  alt="scan preview"
                  className="w-full rounded-lg border border-slate-200"
                  data-testid="ocr-preview-image"
                />
              )}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs font-mono text-amber-900">
                  <span>⏳ TEMPORARY — not added to shift yet</span>
                  <Badge className="bg-amber-200 text-amber-900 border-amber-300">{preview.ocr_mode}</Badge>
                </div>
                <Field label={preview.ocr_mode === "paid_receipt" ? "Store / Supplier" : "Supplier"} v={previewForm.supplier_name} onChange={(v) => setPreviewForm({ ...previewForm, supplier_name: v })} testId="prev-supplier" />
                <Field label={preview.ocr_mode === "paid_receipt" ? "Receipt No. · Αρ. Απόδειξης" : "Invoice No."} v={previewForm.invoice_number} onChange={(v) => setPreviewForm({ ...previewForm, invoice_number: v })} testId="prev-invno" />
                <Field label={preview.ocr_mode === "paid_receipt" ? "Receipt Date" : "Issue Date"} v={previewForm.issue_date} onChange={(v) => setPreviewForm({ ...previewForm, issue_date: v })} testId="prev-issuedate" placeholder="YYYY-MM-DD" />
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Net €" v={previewForm.net_amount} onChange={(v) => setPreviewForm({ ...previewForm, net_amount: v })} testId="prev-net" type="number" />
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">VAT %</Label>
                    <Select value={String(previewForm.vat_rate ?? 0)} onValueChange={(v) => setPreviewForm({ ...previewForm, vat_rate: Number(v) })}>
                      <SelectTrigger className="mt-1" data-testid="prev-vatrate"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="19">19%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Field label="Final Paid €" v={previewForm.total_amount} onChange={(v) => setPreviewForm({ ...previewForm, total_amount: v })} testId="prev-total" type="number" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Payment Status</Label>
                  <Select
                    value={previewForm.status}
                    onValueChange={(v) => {
                      const pm = v === "PAID_CASH" ? "CASH" : v === "PAID_VISA" ? "VISA" : null;
                      setPreviewForm({ ...previewForm, status: v, payment_method: pm });
                    }}
                  >
                    <SelectTrigger className="mt-1" data-testid="prev-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAID_CASH">PAID · CASH</SelectItem>
                      <SelectItem value="PAID_VISA">PAID · VISA</SelectItem>
                      <SelectItem value="UNPAID">UNPAID</SelectItem>
                      <SelectItem value="PENDING_CHEQUE">PENDING · CHEQUE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(previewForm.status === "PAID_VISA" || previewForm.payment_method === "VISA") && (
                  <Field
                    label="VISA Approval / Transaction No. · Νούμερο Έγκρισης"
                    v={previewForm.payment_ref || ""}
                    onChange={(v) => setPreviewForm({ ...previewForm, payment_ref: v })}
                    testId="prev-payment-ref"
                  />
                )}
                {previewForm.status === "PENDING_CHEQUE" && (
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Cheque No." v={previewForm.cheque_number || ""} onChange={(v) => setPreviewForm({ ...previewForm, cheque_number: v })} testId="prev-chqno" />
                    <Field label="Cheque Maturity" v={previewForm.cheque_maturity || ""} onChange={(v) => setPreviewForm({ ...previewForm, cheque_maturity: v })} testId="prev-chqmat" placeholder="YYYY-MM-DD" />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between sm:justify-between gap-2">
            <Button variant="outline" onClick={discardPreview} disabled={committing} data-testid="ocr-preview-discard">
              Discard · Απόρριψη
            </Button>
            <Button
              onClick={commitPreview}
              disabled={committing}
              data-testid="ocr-preview-commit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1"
            >
              <Plus className="h-4 w-4" />
              {committing ? "…" : t("common.addToShift")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          ITER 91 — SPLIT-SCREEN AI-OCR CHEQUE VERIFICATION MODAL
          Auto-opens the moment a cheque-mode scan returns from
          /api/ocr/invoice. Left panel: zoomable cheque image. Right panel:
          editable Amount / Maturity Date / Cheque Number / Bank fields
          pre-filled by the vision pass for Bank of Cyprus / Hellenic Bank
          layouts. Confirming chains into the existing _runCommitPreview
          pipeline → invoice + db.cheques + Master Cheque Calendar event.
      ------------------------------------------------------------------ */}
      <Dialog
        open={chequeVerifyOpen}
        onOpenChange={(o) => {
          if (!o && !committing) {
            setChequeVerifyOpen(false);
            setChequeZoom(1);
          }
        }}
      >
        <DialogContent
          className="max-w-6xl w-[95vw] p-0 overflow-hidden border-amber-300"
          data-testid="cheque-verify-modal"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50">
            <DialogTitle className="flex items-center gap-2 text-amber-900">
              <ShieldCheck className="h-5 w-5" />
              <span data-testid="cheque-verify-title">
                AI Cheque Verification · Επαλήθευση Επιταγής
              </span>
              {preview?.cheque_bank && (
                <Badge
                  className={`ml-2 ${
                    preview.cheque_bank === "BANK_OF_CYPRUS"
                      ? "bg-blue-700 text-white"
                      : preview.cheque_bank === "HELLENIC_BANK"
                      ? "bg-rose-700 text-white"
                      : "bg-slate-600 text-white"
                  }`}
                  data-testid="cheque-verify-bank-badge"
                >
                  {preview.cheque_bank === "BANK_OF_CYPRUS"
                    ? "BANK OF CYPRUS · Τράπεζα Κύπρου"
                    : preview.cheque_bank === "HELLENIC_BANK"
                    ? "HELLENIC BANK · Ελληνική Τράπεζα"
                    : "OTHER BANK · Άλλη Τράπεζα"}
                </Badge>
              )}
            </DialogTitle>
            <p className="text-xs text-amber-700 mt-1">
              Verify the AI-extracted fields against the cheque image. On confirm, the
              maturity date pins onto the Master Cheque Calendar automatically.
              {" · "}
              Επιβεβαιώστε τα στοιχεία και πατήστε «Επιβεβαίωση» για να καρφιτσωθεί η
              ημερομηνία λήξης στο Master Ημερολόγιο Επιταγών.
            </p>
          </DialogHeader>

          {preview && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[480px]">
              {/* LEFT — Zoomable image panel */}
              <div
                className="relative bg-slate-900 flex items-center justify-center overflow-auto"
                data-testid="cheque-verify-left-panel"
              >
                <div className="absolute top-3 left-3 z-10 flex gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setChequeZoom((z) => Math.max(0.5, z - 0.25))}
                    className="bg-white/90 h-8 w-8 p-0"
                    data-testid="cheque-verify-zoom-out"
                    title="Zoom out"
                  >
                    −
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setChequeZoom(1)}
                    className="bg-white/90 h-8 px-2 text-[10px] font-mono"
                    data-testid="cheque-verify-zoom-reset"
                  >
                    {Math.round(chequeZoom * 100)}%
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setChequeZoom((z) => Math.min(4, z + 0.25))}
                    className="bg-white/90 h-8 w-8 p-0"
                    data-testid="cheque-verify-zoom-in"
                    title="Zoom in"
                  >
                    +
                  </Button>
                </div>
                {(preview.cheque_image_url || preview.invoice_image_url) ? (
                  <img
                    src={fileUrl(preview.cheque_image_url || preview.invoice_image_url)}
                    alt="cheque scan"
                    style={{
                      transform: `scale(${chequeZoom})`,
                      transformOrigin: "center center",
                      transition: "transform 120ms ease-out",
                    }}
                    className="max-w-full max-h-[520px] object-contain select-none"
                    data-testid="cheque-verify-image"
                    draggable={false}
                  />
                ) : (
                  <div className="text-slate-400 text-sm">No image available</div>
                )}
              </div>

              {/* RIGHT — Editable fields panel */}
              <div
                className="p-6 space-y-4 bg-white"
                data-testid="cheque-verify-right-panel"
              >
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs font-mono text-amber-900 flex items-center justify-between">
                  <span>⏳ Awaiting your confirmation · Σε αναμονή επιβεβαίωσης</span>
                  <Badge className="bg-amber-200 text-amber-900 border-amber-300">
                    cheque_invoice
                  </Badge>
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    Supplier · Προμηθευτής
                  </Label>
                  <Input
                    value={previewForm.supplier_name || ""}
                    onChange={(e) =>
                      setPreviewForm({ ...previewForm, supplier_name: e.target.value })
                    }
                    className="mt-1"
                    data-testid="cheque-verify-supplier"
                  />
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    Bank · Τράπεζα
                  </Label>
                  <Select
                    value={previewForm.cheque_bank || "OTHER"}
                    onValueChange={(v) =>
                      setPreviewForm({ ...previewForm, cheque_bank: v })
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="cheque-verify-bank">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BANK_OF_CYPRUS">
                        Bank of Cyprus · Τράπεζα Κύπρου
                      </SelectItem>
                      <SelectItem value="HELLENIC_BANK">
                        Hellenic Bank · Ελληνική Τράπεζα
                      </SelectItem>
                      <SelectItem value="OTHER">Other · Άλλη</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Amount € · Ποσό
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={
                        previewForm.cheque_amount ??
                        previewForm.total_amount ??
                        ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setPreviewForm({
                          ...previewForm,
                          cheque_amount: v,
                          total_amount: v,
                        });
                      }}
                      className="mt-1 font-mono text-lg"
                      data-testid="cheque-verify-amount"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Maturity Date · Ημ. Λήξης
                    </Label>
                    <Input
                      type="date"
                      value={previewForm.cheque_maturity || ""}
                      onChange={(e) =>
                        setPreviewForm({
                          ...previewForm,
                          cheque_maturity: e.target.value,
                        })
                      }
                      className="mt-1 font-mono"
                      data-testid="cheque-verify-maturity"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    Cheque Number · Αριθμός Επιταγής
                  </Label>
                  <Input
                    value={previewForm.cheque_number || ""}
                    onChange={(e) =>
                      setPreviewForm({
                        ...previewForm,
                        cheque_number: e.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                    placeholder="6–8 digit MICR serial"
                    className="mt-1 font-mono tracking-widest text-lg"
                    inputMode="numeric"
                    maxLength={10}
                    data-testid="cheque-verify-number"
                  />
                </div>

                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    Issue Date · Ημερομηνία Έκδοσης
                  </Label>
                  <Input
                    type="date"
                    value={previewForm.issue_date || ""}
                    onChange={(e) =>
                      setPreviewForm({ ...previewForm, issue_date: e.target.value })
                    }
                    className="mt-1 font-mono"
                    data-testid="cheque-verify-issue-date"
                  />
                </div>

                <div className="pt-2 text-xs text-slate-500 border-t">
                  <span className="font-semibold text-emerald-700">
                    ✓ Auto-pin enabled.
                  </span>{" "}
                  Confirming below inserts a yellow event onto the Master Cheque
                  Calendar for{" "}
                  <span className="font-mono font-bold">
                    {previewForm.cheque_maturity || "—"}
                  </span>{" "}
                  and routes the cheque to Private Portal OpEx.
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="px-5 py-4 border-t bg-slate-50 gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={() => {
                setChequeVerifyOpen(false);
                setChequeZoom(1);
              }}
              disabled={committing}
              data-testid="cheque-verify-cancel"
            >
              Cancel · Ακύρωση
            </Button>
            <Button
              onClick={async () => {
                // Validate the three critical fields per Iter 91 spec.
                const amount = Number(
                  previewForm.cheque_amount ?? previewForm.total_amount ?? 0
                );
                const maturity = (previewForm.cheque_maturity || "").trim();
                const chqNo = (previewForm.cheque_number || "").trim();
                if (!amount || amount <= 0) {
                  toast.error("Cheque amount is required · Το ποσό είναι υποχρεωτικό");
                  return;
                }
                if (!maturity || !/^\d{4}-\d{2}-\d{2}$/.test(maturity)) {
                  toast.error(
                    "Maturity date is required (YYYY-MM-DD) · Η ημερομηνία λήξης είναι υποχρεωτική"
                  );
                  return;
                }
                if (!chqNo || chqNo.length < 4) {
                  toast.error(
                    "Cheque number is required · Ο αριθμός επιταγής είναι υποχρεωτικός"
                  );
                  return;
                }
                // Ensure the commit payload carries the verified figures + status.
                setPreviewForm((f) => ({
                  ...f,
                  status: "PENDING_CHEQUE",
                  payment_method: "CHEQUE",
                  cheque_amount: amount,
                  total_amount: amount,
                  cheque_maturity: maturity,
                  cheque_number: chqNo,
                }));
                setChequeVerifyOpen(false);
                setChequeZoom(1);
                toast.success(
                  `✓ Verified · Pinning €${amount.toFixed(2)} on ${maturity} to Master Calendar`
                );
                // Chain straight into the existing validation modal which
                // shows the final "Yes, Add Now" confirmation step and then
                // fires _runCommitPreview → /invoices/commit (db.cheques +
                // Master Calendar event auto-pinned server-side).
                await commitPreview();
              }}
              disabled={committing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1"
              data-testid="cheque-verify-confirm"
            >
              <Check className="h-4 w-4" />
              {committing
                ? "…"
                : "Confirm & Pin to Calendar · Επιβεβαίωση"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          CASHFLOW VOUCHER PREVIEW (handed off from Cashflow tab AI Camera).
          Commits via POST /api/cashflow (kind=PAYOUT, category) which fires
          the OpEx payroll handshake server-side.
      ------------------------------------------------------------------ */}
      <Dialog
        open={!!voucherPreview}
        onOpenChange={(o) => !o && !voucherCommitting && discardVoucher()}
      >
        <DialogContent className="max-w-2xl" data-testid="voucher-preview-dialog">
          <DialogHeader>
            <DialogTitle>
              {voucherForm._origin === "manual"
                ? `Cashflow · ${voucherForm.kind === "CASH_DROP" ? "Cash Drop" : "Payout"}`
                : voucherForm._origin === "staff"
                ? "Cashflow · Staff Payment"
                : "Cashflow Voucher · Απόδειξη"}
              {voucherForm.category && voucherForm._origin !== "manual" && ` · ${voucherForm.category}`}
            </DialogTitle>
          </DialogHeader>
          {voucherPreview && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {voucherForm.image_url && !voucherForm.image_url.startsWith("data:") && (
                <img
                  src={fileUrl(voucherForm.image_url)}
                  alt="voucher preview"
                  className="w-full rounded-lg border border-slate-200"
                  data-testid="voucher-preview-image"
                />
              )}
              <div className={`space-y-3 ${voucherForm.image_url && !voucherForm.image_url.startsWith("data:") ? "" : "md:col-span-2"}`}>
                <div className="flex items-center justify-between bg-fuchsia-50 border border-fuchsia-200 rounded-lg px-3 py-2 text-xs font-mono text-fuchsia-900">
                  <span>⏳ TEMPORARY — not added to shift yet</span>
                  <Badge className="bg-fuchsia-200 text-fuchsia-900 border-fuchsia-300">
                    {voucherForm._origin === "manual" ? "manual" : voucherForm._origin === "staff" ? "staff" : "AI scan"}
                  </Badge>
                </div>

                {(voucherForm._origin === "staff" || voucherForm._origin === "voucher") && (
                  <Field
                    label="Employee · Όνομα"
                    v={voucherForm.employee_name || ""}
                    onChange={(v) => setVoucherForm({ ...voucherForm, employee_name: v })}
                    testId="voucher-employee"
                  />
                )}

                <Field
                  label="Amount (€) · Ποσό"
                  v={voucherForm.amount ?? 0}
                  onChange={(v) => setVoucherForm({ ...voucherForm, amount: v })}
                  testId="voucher-amount"
                  type="number"
                />

                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                    Payment Source · Πηγή Πληρωμής
                  </Label>
                  <Select
                    value={voucherForm.payment_source || "DRAWER"}
                    onValueChange={(v) => setVoucherForm({ ...voucherForm, payment_source: v })}
                  >
                    <SelectTrigger
                      className={`mt-1 ${
                        (voucherForm.payment_source || "DRAWER") === "EXTERNAL"
                          ? "border-2 border-sky-400 bg-sky-50 text-sky-900 font-bold"
                          : ""
                      }`}
                      data-testid="voucher-payment-source"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRAWER" data-testid="voucher-payment-source-drawer">
                        Cash from Drawer · Από Ταμείο
                      </SelectItem>
                      <SelectItem value="EXTERNAL" data-testid="voucher-payment-source-external">
                        External / Bank Transfer · Εκτός Ταμείου
                      </SelectItem>
                      <SelectItem value="CHEQUE" data-testid="voucher-payment-source-cheque">
                        Cheque · Επιταγή
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1 leading-snug" data-testid="voucher-payment-source-hint">
                    {(voucherForm.payment_source || "DRAWER") === "EXTERNAL"
                      ? "Will NOT subtract from drawer · routed to Portal OpEx & Tax Matrix"
                      : (voucherForm.payment_source || "DRAWER") === "CHEQUE"
                      ? "Bypasses the drawer · mirrors to Master Cheque Calendar & OpEx"
                      : "Strict drawer queue · subtracts from Expected Cash at End Shift"}
                  </p>
                </div>

                {(voucherForm.payment_source || "DRAWER") === "CHEQUE" && (
                  <div className="grid grid-cols-3 gap-2" data-testid="voucher-cheque-fields">
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">Cheque #</Label>
                      <Input
                        value={voucherForm.cheque_number || ""}
                        onChange={(e) => setVoucherForm({ ...voucherForm, cheque_number: e.target.value })}
                        placeholder="123456"
                        className="font-mono"
                        data-testid="voucher-cheque-number"
                      />
                    </div>
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">Maturity · Λήξη</Label>
                      <Input
                        type="date"
                        value={voucherForm.cheque_maturity || ""}
                        onChange={(e) => setVoucherForm({ ...voucherForm, cheque_maturity: e.target.value })}
                        data-testid="voucher-cheque-maturity"
                      />
                    </div>
                    <div>
                      <Label className="text-[9px] uppercase tracking-wider text-slate-500">Payee · Δικαιούχος</Label>
                      <Input
                        value={voucherForm.cheque_supplier || ""}
                        onChange={(e) => setVoucherForm({ ...voucherForm, cheque_supplier: e.target.value })}
                        placeholder="Supplier / payee"
                        data-testid="voucher-cheque-supplier"
                      />
                    </div>
                  </div>
                )}

                {ownerActive && (
                  <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="voucher-backdate-block">
                    <Label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">
                      🔓 Master Backdate · Custom Transaction Date
                    </Label>
                    <Input
                      type="date"
                      value={voucherForm.transaction_date || ""}
                      onChange={(e) => setVoucherForm({ ...voucherForm, transaction_date: e.target.value })}
                      max={new Date().toISOString().slice(0, 10)}
                      data-testid="voucher-transaction-date"
                      className="mt-2 bg-white border-amber-300"
                    />
                    <p className="text-[10px] text-amber-700 mt-1 leading-snug">
                      Owner only · routes this entry to Portal OpEx + Tax Matrix for the
                      matching past quarter · keeps the live €700 float untouched.
                    </p>
                  </div>
                )}

                {voucherForm._origin === "manual" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Movement Type · Τύπος
                    </Label>
                    <Select
                      value={voucherForm.kind || "PAYOUT"}
                      onValueChange={(v) => setVoucherForm({ ...voucherForm, kind: v })}
                    >
                      <SelectTrigger className="mt-1" data-testid="voucher-kind">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PAYOUT">Payout · Πληρωμή</SelectItem>
                        <SelectItem value="CASH_DROP">Cash Drop · Μεταφορά</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {(voucherForm._origin === "staff" || voucherForm._origin === "voucher") && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                      Category · Κατηγορία
                    </Label>
                    <Select
                      value={voucherForm.category || ""}
                      onValueChange={(v) => setVoucherForm({ ...voucherForm, category: v })}
                    >
                      <SelectTrigger
                        className="mt-1"
                        data-testid="voucher-category"
                      >
                        <SelectValue placeholder="Pick a category…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Salary Payment">Salary Payment · Μισθοδοσία</SelectItem>
                        <SelectItem value="Salary Advance">Salary Advance · Προκαταβολή</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Field
                  label="Note"
                  v={voucherForm.note || ""}
                  onChange={(v) => setVoucherForm({ ...voucherForm, note: v })}
                  testId="voucher-note"
                />
                {voucherForm.raw_text && (
                  <details className="text-[11px] font-mono text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                    <summary className="cursor-pointer select-none">OCR raw text</summary>
                    <pre className="whitespace-pre-wrap mt-2">{voucherForm.raw_text}</pre>
                  </details>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={discardVoucher}
              disabled={voucherCommitting}
              data-testid="voucher-discard"
            >
              Discard · Απόρριψη
            </Button>
            <Button
              onClick={commitVoucher}
              disabled={voucherCommitting}
              data-testid="voucher-commit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1"
            >
              <Plus className="h-4 w-4" />
              {voucherCommitting ? "…" : t("common.addToShift")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 25 — AI Match modal (Cheque → Credit-Invoice cross-reference) */}
      <Dialog open={aiMatchOpen} onOpenChange={(o) => !o && setAiMatchOpen(false)}>
        <DialogContent
          className="max-w-lg border-2 border-emerald-400"
          data-testid="ai-match-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-900">
              <Check className="h-6 w-6 text-emerald-600" />
              <span className="text-base sm:text-lg leading-tight">
                🔗 ΑΥΤΟΜΑΤΗ ΣΥΝΔΕΣΗ / AI MATCH FOUND!
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-[13px] leading-relaxed">
            <p className="text-slate-800 font-semibold">
              Ανιχνεύθηκε απλήρωτο τιμολόγιο επί πιστώσει για τον προμηθευτή <strong>{previewForm?.supplier_name || preview?.supplier_name || "—"}</strong> με ποσό <strong>€{Number(previewForm?.total_amount || preview?.total_amount || 0).toFixed(2)}</strong>. Θέλετε αυτή η Επιταγή να εξοφλήσει το συγκεκριμένο τιμολόγιο;
            </p>
            <p className="text-slate-600">
              Unpaid credit invoice found for this supplier and amount. Link this cheque to close that outstanding balance?
            </p>

            {aiMatches.length > 0 && (
              <div
                className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-[12px] font-mono text-slate-700 space-y-1"
                data-testid="ai-match-summary"
              >
                {aiMatches.slice(0, 3).map((m) => (
                  <div key={m.id} className="flex justify-between" data-testid={`ai-match-row-${m.id}`}>
                    <span>
                      <strong>{m.supplier_name}</strong> · #{m.invoice_number || "—"} · {m.issue_date || "—"}
                    </span>
                    <span className="font-semibold">€{Number(m.total_amount || 0).toFixed(2)} · {m.status}</span>
                  </div>
                ))}
                {aiMatches.length > 3 && (
                  <div className="text-[11px] text-emerald-700 italic">… +{aiMatches.length - 3} more match(es); first match will be linked.</div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-4">
            <Button
              onClick={() => {
                setLinkInvoiceId(aiMatches[0]?.id || null);
                setAiMatchOpen(false);
                openShiftValidation(true);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 text-base gap-2 shadow-md"
              data-testid="ai-match-link"
            >
              <Check className="h-5 w-5" />
              🟢 Ναι, Σύνδεση & Εξόφληση / Yes, Link & Close
            </Button>
            <button
              type="button"
              onClick={() => {
                setLinkInvoiceId(null);
                setAiMatchOpen(false);
                openShiftValidation(true);
              }}
              className="text-rose-700 hover:text-rose-800 hover:underline font-semibold text-sm px-3 py-2 transition-colors"
              data-testid="ai-match-skip"
            >
              🔴 Όχι, είναι νέα κίνηση / No, New Entry
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter 23 — Shift-Validation modal (shared by Invoice OCR + Voucher commits) */}
      {/* Iter 24 — Branches into the dedicated Cheque-Detected layout when */}
      {/*           the active preview is a cheque (ocr_mode/status/cheque#).  */}
      <Dialog open={validationOpen} onOpenChange={(o) => !o && dismissValidationAsDraft()}>
        <DialogContent
          className={`max-w-lg border-2 ${validationMode === "cheque" ? "border-sky-400" : "border-amber-400"}`}
          data-testid={validationMode === "cheque" ? "cheque-validation-dialog" : "invoice-validation-dialog"}
        >
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${validationMode === "cheque" ? "text-sky-900" : "text-amber-900"}`}>
              <AlertTriangle className={`h-6 w-6 ${validationMode === "cheque" ? "text-sky-600" : "text-amber-600"}`} />
              <span className="text-base sm:text-lg leading-tight">
                {validationMode === "cheque"
                  ? "🚨 ΑΝΙΧΝΕΥΘΗΚΕ ΕΠΙΤΑΓΗ / CHEQUE DETECTED"
                  : "🚨 ΕΠΙΚΥΡΩΣΗ ΒΑΡΔΙΑΣ / SHIFT VALIDATION"}
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-[13px] leading-relaxed">
            {validationMode === "cheque" ? (
              <>
                <p className="text-slate-800 font-semibold">
                  Το AI-OCR αναγνώρισε πληρωμή με Επιταγή! Το σύστημα θα εφαρμόσει αυτόματα το Cheque Bypass με 0.00 επίπτωση στα μετρητά και η βάση των €0.00 θα παραμείνει ανέπαφη. Θέλετε να δρομολογηθεί ΤΩΡΑ στο Master Cheque Calendar;
                </p>
                <p className="text-slate-600">
                  Cheque detected via AI! System will apply Cheque Bypass with 0.00 shift effect, leaving the €0.00 float untouched. Route to Master Calendar now?
                </p>
              </>
            ) : (
              <>
                <p className="text-slate-800 font-semibold">
                  Το τιμολόγιο αποθηκεύτηκε επιτυχώς! Θέλετε να ολοκληρωθεί η προσθήκη ΤΩΡΑ στα έξοδα της βάρδιας για να αφαιρεθεί αυτόματα το ποσό από τα μετρητά του συρταριού;
                </p>
                <p className="text-slate-600">
                  Invoice saved successfully! Do you want to finalize adding it to the active shift layout right now to auto-deduct the cash?
                </p>
              </>
            )}

            {pendingCommit?.summary && (
              <div
                className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-3 text-[12px] font-mono text-slate-700"
                data-testid={validationMode === "cheque" ? "cheque-validation-summary" : "invoice-validation-summary"}
              >
                <div className="flex justify-between">
                  <span className="text-slate-500">Type</span>
                  <span className="font-semibold">{pendingCommit.summary.kind}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Supplier / Ref</span>
                  <span className="font-semibold truncate max-w-[60%] text-right">{pendingCommit.summary.supplier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Amount</span>
                  <span className="font-semibold">€{Number(pendingCommit.summary.amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">
                    {validationMode === "cheque" ? "Cheque #" : "Status / Source"}
                  </span>
                  <span className="font-semibold">{pendingCommit.summary.status}</span>
                </div>
                {validationMode === "cheque" && pendingCommit.summary.maturity && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Maturity</span>
                    <span className="font-semibold">{pendingCommit.summary.maturity}</span>
                  </div>
                )}
                {validationMode === "cheque" && (
                  <div className="mt-2 pt-2 border-t border-sky-200 text-[11px] text-sky-800">
                    🛡️ Cheque Bypass active · Drawer impact: <strong>€0.00</strong> · Auto-routes to Master Cheque Calendar.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-4">
            <Button
              onClick={acceptValidationAndCommit}
              disabled={committing || voucherCommitting}
              className={`text-white font-bold h-12 text-base gap-2 shadow-md ${
                validationMode === "cheque"
                  ? "bg-sky-600 hover:bg-sky-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
              data-testid={validationMode === "cheque" ? "cheque-validation-route" : "invoice-validation-add"}
            >
              <Check className="h-5 w-5" />
              {validationMode === "cheque"
                ? "🟢 Ναι, Δρομολόγηση Τώρα / Yes, Route Now"
                : "🟢 Ναι, Προσθήκη Τώρα / Yes, Add Now"}
            </Button>
            <button
              type="button"
              onClick={dismissValidationAsDraft}
              disabled={committing || voucherCommitting}
              className="text-rose-700 hover:text-rose-800 hover:underline font-semibold text-sm px-3 py-2 transition-colors disabled:opacity-50"
              data-testid={validationMode === "cheque" ? "cheque-validation-later" : "invoice-validation-later"}
            >
              🔴 Όχι, αργότερα / No, later
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------- LEFT COLUMN COMPONENTS -----------------
function ActiveShiftItems({ invoices, cashflow, onDeleteInvoice, onDeleteCashflow, onEditInvoice }) {
  // Unified view — combine invoices and cashflow rows into a single time-sorted
  // ledger so the operator sees the chronological order they were added.
  const rows = [
    ...invoices.map((i) => ({
      _type: "invoice",
      id: i.id,
      ts: i.created_at,
      title: i.supplier_name || "Unknown supplier",
      sub: `${i.invoice_number || "—"} · ${(i.status || "").replace("_", " · ")}`,
      amount: i.total_amount || 0,
      raw: i,
    })),
    ...cashflow.map((c) => ({
      _type: "cashflow",
      id: c.id,
      ts: c.created_at,
      title: c.category
        ? `${c.employee_name || "Staff"} · ${c.category}`
        : c.kind === "PAYOUT" ? "Payout" : "Cash Drop",
      sub: c.note || "—",
      amount: c.amount || 0,
      raw: c,
    })),
  ].sort((a, b) => (a.ts < b.ts ? 1 : -1));

  const totalOut = rows.reduce((s, r) => {
    if (r._type === "cashflow" && r.raw.kind === "PAYOUT") return s + Number(r.amount || 0);
    if (r._type === "invoice" && (r.raw.status === "PAID_CASH" || r.raw.status === "PAID_VISA")) return s + Number(r.amount || 0);
    return s;
  }, 0);

  return (
    <Card className="p-6" data-testid="active-shift-items">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Active Shift Items · Στοιχεία Ενεργής Βάρδιας</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Everything committed to this register's shift. Edit or delete to auto-reverse the math.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">total cash out</div>
          <div className="font-mono font-bold text-lg text-orange-700" data-testid="shift-items-total">€{totalOut.toFixed(2)}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-6 border-2 border-dashed border-slate-200 rounded-lg" data-testid="shift-items-empty">
          Nothing added to this shift yet.
        </div>
      ) : (
        <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
          {rows.map((r) => (
            <div
              key={`${r._type}-${r.id}`}
              data-testid={`shift-item-${r._type}-${r.id}`}
              className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
            >
              <Badge
                className={`text-[10px] font-bold uppercase ${
                  r._type === "invoice"
                    ? "bg-blue-100 text-blue-700 border-blue-200"
                    : r.raw.kind === "CASH_DROP"
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-orange-100 text-orange-700 border-orange-200"
                }`}
              >
                {r._type === "invoice" ? "Invoice" : r.raw.kind === "CASH_DROP" ? "Drop" : "Payout"}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-900 truncate">{r.title}</div>
                <div className="text-[11px] font-mono text-slate-500 truncate">{r.sub}</div>
              </div>
              <div className="font-mono font-bold text-slate-900 text-sm w-20 text-right">€{Number(r.amount).toFixed(2)}</div>
              {r._type === "invoice" && (
                <button
                  onClick={() => onEditInvoice(r.raw)}
                  data-testid={`shift-item-edit-${r.id}`}
                  className="h-8 w-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-200"
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => (r._type === "invoice" ? onDeleteInvoice(r.raw) : onDeleteCashflow(r.raw))}
                data-testid={`shift-item-delete-${r._type}-${r.id}`}
                className="h-8 w-8 flex items-center justify-center rounded-md text-red-600 hover:text-white hover:bg-red-600 transition-colors"
                title="Delete & reverse math"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function RecentInvoices({ invoices, onOpen }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <ReceiptText className="h-5 w-5" />
          Recent Invoices
        </h2>
        <span className="text-xs text-slate-500 font-mono">{invoices.length} total · sorted by issue date</span>
      </div>
      {invoices.length === 0 ? (
        <Card className="p-6 text-center text-slate-500 border-dashed border-2" data-testid="invoices-empty">
          No invoices yet. Tap the camera button (bottom-left) to scan an <span className="font-semibold">Invoice + Cheque</span> or a <span className="font-semibold">Paid Receipt</span> stamp.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {invoices.slice(0, 6).map((inv) => {
            const meta = STATUS_META[inv.status] || STATUS_META.UNPAID;
            return (
              <button
                key={inv.id}
                onClick={() => onOpen(inv)}
                data-testid={`invoice-card-${inv.id}`}
                className="text-left bg-white border border-slate-200 rounded-xl p-3 hover:shadow-md transition-shadow flex gap-3"
              >
                {inv.invoice_image_url ? (
                  <img src={fileUrl(inv.invoice_image_url)} alt="" className="h-14 w-14 object-cover rounded-md border" />
                ) : (
                  <div className="h-14 w-14 rounded-md bg-slate-100 flex items-center justify-center text-slate-400">
                    <ReceiptText className="h-6 w-6" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{inv.supplier_name || "Unknown supplier"}</div>
                  <div className="text-[11px] font-mono text-slate-500 truncate">{inv.invoice_number || "—"} · {inv.issue_date || "—"}</div>
                  <div className="text-[11px] font-mono text-slate-700 truncate mt-0.5" title={formatPaymentLine(inv)}>
                    {formatPaymentLine(inv)}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="font-mono font-bold text-slate-900 text-sm">€{inv.total_amount.toFixed(2)}</span>
                    <Badge className={`${meta.className} border font-semibold text-[10px]`}>{meta.label}</Badge>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Iter 80 · Daily Store Subfolders — handwritten spec table
// 6 columns: INVOICE/PDF · ΟΝΟΜΑ ΠΡΟΜΗΘΕΥΤΗ · PHOTO CHEQUE · DATE · TOTAL · STATUS
// Iter 113 — Tenant-safe: tab labels come 100% from the backend response
// `valid_locations` (which is scoped to the caller's tenant), so a fresh
// Owner never sees `Vizantiou / Stavrou / Kallipoleos / Aglantzia`.
// ──────────────────────────────────────────────────────────────────────

function DailyStoreFolders({ onOpenInvoice, onInvoiceDeleted }) {
  const [storeTabs, setStoreTabs] = useState([]);
  const [activeStore, setActiveStore] = useState(null);
  const [openDate, setOpenDate] = useState(null);     // currently expanded date
  // Iter 95 — Per-shift expansion. Tracks which Βάρδια inside the open
  // date the operator has drilled into. `null` = show the shift list
  // only; a shift_id (or 'unassigned') = render that shift's
  // CategorisedTables view filtered to ONLY that shift's rows.
  const [openShift, setOpenShift] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chequePreview, setChequePreview] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/invoices/daily-folders")
      .then(({ data }) => {
        setFolders(Array.isArray(data?.folders) ? data.folders : []);
        const nextTabs = Array.isArray(data?.valid_locations)
          ? data.valid_locations
          : [];
        setStoreTabs(nextTabs);
        setActiveStore((prev) =>
          prev && nextTabs.includes(prev) ? prev : (nextTabs[0] || null),
        );
      })
      .catch(() => { /* fail-soft */ })
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  // Iter 114 · Row-level delete of individual invoice / cheque / photo
  // inside a Daily Folder. Confirms in Greek, DELETE /invoices/{id} which
  // cascades opex + linked cheque, then refreshes both the top-level list
  // (via onInvoiceDeleted) and the daily-folder tree.
  const handleRowDelete = async (inv, kind = "invoice") => {
    if (!inv?.id) return;
    const label =
      inv.supplier_name || inv.invoice_number || inv.id;
    const amount = Number(inv.total_amount || 0).toFixed(2);
    const kindLabel =
      kind === "cheque" ? "την επιταγή" :
      kind === "photo" ? "τη φωτογραφία" :
      "το τιμολόγιο";
    const ok = window.confirm(
      `⚠️ Θέλετε να διαγράψετε οριστικά ${kindLabel} «${label}» (€${amount});\n\n` +
      "Η ενέργεια θα αντιστρέψει και τα συνδεδεμένα OpEx/Cheque/Photo entries και θα καταγραφεί στο audit log.",
    );
    if (!ok) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      reload();
      if (typeof onInvoiceDeleted === "function") onInvoiceDeleted();
    } catch (err) {
      window.alert(
        "Αποτυχία διαγραφής: " +
        (err?.response?.data?.detail || err.message || "unknown"),
      );
    }
  };

  const storeFolder = folders.find((f) => f.location === activeStore) || {
    dates: [],
    total_invoices: 0,
    subfolder_count: 0,
  };

  return (
    <section className="space-y-3" data-testid="daily-store-folders">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <Layers className="h-5 w-5" />
          📁 DAILY FOLDERS · Ημερήσιοι Φάκελοι
        </h2>
        <button
          onClick={reload}
          className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
          data-testid="daily-folders-refresh"
          title="Refresh"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      {/* Store tabs */}
      <div className="flex flex-wrap gap-1.5" data-testid="daily-folders-tabs">
        {storeTabs.length === 0 ? (
          <div className="text-xs italic text-slate-500" data-testid="daily-folders-no-stores">
            {/* Iter 113 · Fresh Owner with zero configured kiosks. Guide
                them to Settings → Add your first store instead of leaking
                the master chain names. */}
            Δεν υπάρχει ακόμη κατάστημα. Πρόσθεσε το πρώτο σου κατάστημα από τις Ρυθμίσεις.
          </div>
        ) : storeTabs.map((store) => {
          const folder = folders.find((f) => f.location === store);
          const isActive = store === activeStore;
          return (
            <button
              key={store}
              data-testid={`daily-tab-${store.toLowerCase()}`}
              onClick={() => { setActiveStore(store); setOpenDate(null); setOpenShift(null); }}
              className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${
                isActive
                  ? "bg-slate-900 text-white shadow"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {store}
              <span className={`ml-2 text-[10px] font-mono ${isActive ? "text-amber-300" : "text-slate-400"}`}>
                {folder?.total_invoices ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <Card className="p-6 text-center text-slate-500 border-dashed border-2">Loading folders…</Card>
      ) : storeFolder.dates.length === 0 ? (
        <Card
          className="p-6 text-center text-slate-500 border-dashed border-2"
          data-testid="daily-folders-empty"
        >
          {/* Iter 106 spec §2 — bilingual empty-state literal so the
              layout is calm and honest when the DB is truly empty. */}
          <div className="text-base font-semibold text-slate-700">
            Δεν βρέθηκαν καταχωρήσεις
          </div>
          <div className="text-sm mt-1">
            No invoices yet for <strong>{activeStore}</strong>.
          </div>
        </Card>
      ) : (
        <div className="space-y-2" data-testid={`daily-dates-${activeStore.toLowerCase()}`}>
          {storeFolder.dates.map((subf) => {
            const isOpen = openDate === subf.date;
            return (
              <div
                key={subf.date}
                className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                data-testid={`daily-subfolder-${subf.date}`}
              >
                <button
                  onClick={() => {
                    setOpenDate(isOpen ? null : subf.date);
                    setOpenShift(null);  // Iter 95 — reset drill-down when date toggles
                  }}
                  data-testid={`daily-subfolder-toggle-${subf.date}`}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className={`h-4 w-4 ${isOpen ? "text-amber-600" : "text-slate-500"}`} />
                    <span className="font-mono text-sm font-bold text-slate-900">
                      {subf.date === "—" ? "(undated)" : subf.date}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {subf.count} invoice{subf.count !== 1 ? "s" : ""}
                    </span>
                    {/* Iter 95 — shift count badge */}
                    {subf.shift_count > 0 && (
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5"
                        data-testid={`daily-subfolder-shift-count-${subf.date}`}
                      >
                        {subf.shift_count} βάρδι{subf.shift_count === 1 ? "α" : "ες"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 font-mono text-sm font-bold text-slate-700">
                    €{subf.total.toFixed(2)}
                    {/* Iter 114 · Cascading delete of this date's folder */}
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`daily-folder-delete-${subf.date}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const ok = window.confirm(
                          "⚠️ ΠΡΟΣΟΧΗ! Αυτή η ενέργεια θα διαγράψει οριστικά τον καθημερινό φάκελο ΚΑΙ ΟΛΑ τα αρχεία, φωτογραφίες, Cheques και Invoices που περιέχονται μέσα του. Θέλετε να προχωρήσετε;",
                        );
                        if (!ok) return;
                        try {
                          await api.delete(
                            `/kiosks/${encodeURIComponent(activeStore)}/daily-folders/${encodeURIComponent(subf.date)}`,
                          );
                          reload();
                        } catch (err) {
                          window.alert(
                            "Αποτυχία διαγραφής: " +
                            (err?.response?.data?.detail || err.message),
                          );
                        }
                      }}
                      title="Διαγραφή Ημερήσιου Φακέλου"
                      className="inline-flex items-center rounded-md border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700 hover:bg-rose-100"
                    >
                      🗑️
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-200">
                    {/* Iter 95 — Infinite Dynamic Shift-Isolation rows.
                        When the date carries one or more shifts we
                        render the per-shift expansion list FIRST. Each
                        shift row drills down into a fully-isolated
                        CategorisedTables view scoped to that shift only.
                        When no shifts attribute, we fall back to the
                        legacy categorised-by-date view. */}
                    {Array.isArray(subf.shifts) && subf.shifts.length > 0 ? (
                      <div className="divide-y divide-slate-100" data-testid={`shift-list-${subf.date}`}>
                        {subf.shifts.map((sh) => {
                          const shiftKey = sh.shift_id || `unassigned-${subf.date}`;
                          const isShiftOpen = openShift === shiftKey;
                          return (
                            <div key={shiftKey} className="bg-white">
                              <button
                                onClick={() => setOpenShift(isShiftOpen ? null : shiftKey)}
                                data-testid={`shift-toggle-${shiftKey}`}
                                className={`w-full px-5 py-2.5 flex items-center justify-between hover:bg-amber-50/40 transition-colors ${
                                  isShiftOpen ? "bg-amber-50/60" : ""
                                }`}
                              >
                                <div className="flex items-center gap-3 text-left">
                                  <span
                                    className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ${
                                      sh.shift_id
                                        ? "bg-amber-200 text-amber-900 border border-amber-400"
                                        : "bg-slate-200 text-slate-600 border border-slate-300"
                                    }`}
                                  >
                                    {sh.shift_number || "?"}
                                  </span>
                                  <span
                                    className="font-mono text-sm font-bold text-slate-900"
                                    data-testid={`shift-label-${shiftKey}`}
                                  >
                                    {sh.display_label || sh.shift_label || `Βάρδια ${sh.shift_number}`}
                                  </span>
                                  <span className="text-[10px] text-slate-500 font-mono">
                                    {sh.invoice_count} inv · {sh.cashflow_count} opex
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="font-mono text-xs text-slate-600">
                                    €{(sh.invoice_total + sh.cashflow_total).toFixed(2)}
                                  </span>
                                  <span className="text-slate-400 text-sm">
                                    {isShiftOpen ? "▾" : "▸"}
                                  </span>
                                </div>
                              </button>
                              {isShiftOpen && (
                                <div
                                  className="border-t border-amber-100 bg-gradient-to-b from-amber-50/20 to-white"
                                  data-testid={`shift-detail-${shiftKey}`}
                                >
                                  {/* Z-Report + VISA Slip thumbnails for the shift */}
                                  {(sh.zreport_image_url || sh.visa_slip_image_url || sh.shift_id) && (
                                    <div className="px-4 pt-3 pb-2 flex flex-wrap gap-2 text-[11px]">
                                      {sh.zreport_image_url && (
                                        <button
                                          onClick={() => setChequePreview(sh.zreport_image_url)}
                                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-rose-100 text-rose-800 hover:bg-rose-200 font-mono"
                                          data-testid={`shift-zreport-${shiftKey}`}
                                        >
                                          🧾 Z-Report
                                        </button>
                                      )}
                                      {sh.visa_slip_image_url && (
                                        <button
                                          onClick={() => setChequePreview(sh.visa_slip_image_url)}
                                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200 font-mono"
                                          data-testid={`shift-visa-${shiftKey}`}
                                        >
                                          💳 VISA Slip
                                        </button>
                                      )}
                                      {/* Iter 104 — Green WhatsApp ZIP dispatch button.
                                          Compiles the shift's full attachment folder +
                                          shift_summary.json into a .zip, mints a
                                          short URL, and opens a wa.me deep link to
                                          John's official Iter 104 number (35796391092). */}
                                      {sh.shift_id && (
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const t = toast.loading("📦 Compiling shift ZIP…");
                                            try {
                                              const { data } = await api.post(
                                                `/shifts/${sh.shift_id}/whatsapp-zip`,
                                              );
                                              try {
                                                if (navigator.clipboard && navigator.clipboard.writeText) {
                                                  await navigator.clipboard.writeText(data.wa_text || data.short_url);
                                                }
                                              } catch { /* clipboard denied — fine */ }
                                              try {
                                                window.open(data.wa_url, "_blank", "noopener,noreferrer");
                                              } catch { /* popup blocked — silent per Iter 96 */ }
                                              toast.success(
                                                `✓ ZIP ready · ${(data.size_bytes / 1024).toFixed(0)} KB · ${data.photo_count} photo(s) → WhatsApp`,
                                                { id: t },
                                              );
                                            } catch (err) {
                                              const detail = err?.response?.data?.detail || err?.message || "ZIP dispatch failed";
                                              toast.error(`🧯 ${detail}`, { id: t });
                                            }
                                          }}
                                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider text-[10px] shadow"
                                          data-testid={`shift-whatsapp-zip-${shiftKey}`}
                                          title="Iter 104 · WhatsApp ZIP dispatch"
                                        >
                                          🟢 ΑΠΟΣΤΟΛΗ ZIP ΣΤΟ WHATSAPP
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  {/* Categorised invoice rows scoped to this shift */}
                                  {Array.isArray(sh.categories) && sh.categories.length > 0 ? (
                                    <CategorisedTables
                                      date={sh.display_label || `${subf.date} · ${sh.shift_folder || ""}`}
                                      categories={sh.categories}
                                      onChequeClick={(url) => setChequePreview(url)}
                                      onOpenInvoice={onOpenInvoice}
                                      onDelete={handleRowDelete}
                                    />
                                  ) : sh.invoice_count > 0 && Array.isArray(subf.invoices) && subf.invoices.length > 0 ? (
                                    /* Iter 114 · Fallback for shifts whose server payload
                                       omits categories but still carries invoice_count>0
                                       (legacy / orphan shifts). Filter the date's flat
                                       invoice list by shift_id so the 🗑️ delete icon is
                                       still reachable per row. */
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs" data-testid={`daily-shift-table-${shiftKey}`}>
                                        <SpecTableHead />
                                        <tbody>
                                          {subf.invoices
                                            .filter((inv) => !sh.shift_id || inv.shift_id === sh.shift_id)
                                            .map((inv) => (
                                              <DailyFolderRow
                                                key={inv.id}
                                                inv={inv}
                                                onChequeClick={(url) => setChequePreview(url)}
                                                onOpenInvoice={onOpenInvoice}
                                                onDelete={handleRowDelete}
                                              />
                                            ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="px-4 py-3 text-[11px] text-slate-400 italic">
                                      No invoices logged in this shift · Καμία καταχώρηση
                                    </div>
                                  )}
                                  {/* Cashflow OpEx rows for the shift */}
                                  {sh.cashflow.length > 0 && (
                                    <div className="px-4 pb-4">
                                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
                                        💸 Cashflow OpEx · {sh.cashflow_count} row{sh.cashflow_count !== 1 ? "s" : ""}
                                      </div>
                                      <table
                                        className="w-full text-[11px] font-mono"
                                        data-testid={`shift-cashflow-table-${shiftKey}`}
                                      >
                                        <thead className="bg-slate-100">
                                          <tr className="text-left text-slate-600">
                                            <th className="px-2 py-1">Category</th>
                                            <th className="px-2 py-1">Note</th>
                                            <th className="px-2 py-1">Method</th>
                                            <th className="px-2 py-1 text-right">€</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {sh.cashflow.map((c) => (
                                            <tr key={c.id} className="border-t border-slate-100">
                                              <td className="px-2 py-1">{c.category}</td>
                                              <td className="px-2 py-1 truncate max-w-[280px]">{c.note}</td>
                                              <td className="px-2 py-1 text-slate-500">{c.payment_method}</td>
                                              <td className="px-2 py-1 text-right">€{c.amount.toFixed(2)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : subf.categories && subf.categories.length > 0 ? (
                      <CategorisedTables
                        date={subf.date}
                        categories={subf.categories}
                        onChequeClick={(url) => setChequePreview(url)}
                        onOpenInvoice={onOpenInvoice}
                        onDelete={handleRowDelete}
                      />
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid={`daily-table-${subf.date}`}>
                          <SpecTableHead />
                          <tbody>
                            {subf.invoices.map((inv) => (
                              <DailyFolderRow
                                key={inv.id}
                                inv={inv}
                                onChequeClick={(url) => setChequePreview(url)}
                                onOpenInvoice={onOpenInvoice}
                                onDelete={handleRowDelete}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* PHOTO CHEQUE thumbnail modal */}
      <Dialog open={!!chequePreview} onOpenChange={() => setChequePreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="cheque-preview-modal">
          <DialogHeader>
            <DialogTitle>📎 Cheque photo / Φωτογραφία επιταγής</DialogTitle>
          </DialogHeader>
          {chequePreview && (
            <img
              src={fileUrl(chequePreview)}
              alt="cheque"
              className="w-full max-h-[70vh] object-contain rounded-md"
              data-testid="cheque-preview-img"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}


// Iter 80b — Spec table header (extracted so both flat + categorised views share it)
function SpecTableHead() {
  return (
    <thead className="bg-slate-100 text-slate-600 uppercase tracking-wider">
      <tr>
        <th className="px-3 py-2 text-left font-semibold">INVOICE / PDF</th>
        <th className="px-3 py-2 text-left font-semibold">ΟΝΟΜΑ ΠΡΟΜΗΘΕΥΤΗ</th>
        <th className="px-3 py-2 text-center font-semibold">PHOTO CHEQUE</th>
        <th className="px-3 py-2 text-left font-semibold">DATE</th>
        <th className="px-3 py-2 text-right font-semibold">TOTAL</th>
        <th className="px-3 py-2 text-center font-semibold">STATUS</th>
      </tr>
    </thead>
  );
}

// Iter 80b — Categorised tables rendered inside an expanded date subfolder.
// One pill per emitted category (Z-Reports / VISA / Invoices / SalaryAdvance /
// Bills / Uncategorized_Receipts). Invoices category further drills into one
// per-supplier subfolder. The 6-column spec table is rendered identically
// inside every leaf.
const CATEGORY_META = {
  "Z-Reports": { icon: "🧾", color: "bg-amber-100 text-amber-800 ring-amber-300" },
  "VISA": { icon: "💳", color: "bg-indigo-100 text-indigo-800 ring-indigo-300" },
  "Invoices": { icon: "📄", color: "bg-blue-100 text-blue-800 ring-blue-300" },
  "SalaryAdvance": { icon: "💼", color: "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-300" },
  "Bills": { icon: "⚡", color: "bg-orange-100 text-orange-800 ring-orange-300" },
  "Uncategorized_Receipts": { icon: "📂", color: "bg-slate-100 text-slate-700 ring-slate-300" },
};

function CategorisedTables({ date, categories, onChequeClick, onOpenInvoice, onDelete }) {
  const [activeCat, setActiveCat] = useState(categories[0]?.key || null);
  const [openSub, setOpenSub] = useState(null);

  useEffect(() => {
    setActiveCat(categories[0]?.key || null);
    setOpenSub(null);
  }, [date, categories]);

  const activeCategory = categories.find((c) => c.key === activeCat) || categories[0];

  return (
    <div className="p-3 bg-slate-50/60" data-testid={`daily-categories-${date}`}>
      {/* Category pills */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`daily-category-tabs-${date}`}>
        {categories.map((c) => {
          const meta = CATEGORY_META[c.key] || CATEGORY_META.Uncategorized_Receipts;
          const isActive = c.key === activeCat;
          return (
            <button
              key={c.key}
              onClick={() => { setActiveCat(c.key); setOpenSub(null); }}
              data-testid={`daily-category-${date}-${c.key}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ring-1 transition-all ${
                isActive ? `${meta.color} shadow-sm` : "bg-white text-slate-500 ring-slate-200 hover:ring-slate-400"
              }`}
            >
              {meta.icon} {c.label} <span className="ml-1 font-mono">{c.count}</span>
            </button>
          );
        })}
      </div>

      {/* Active category body */}
      {activeCategory && (
        <div className="space-y-2">
          {activeCategory.subfolders.map((sub) => {
            const isInvoicesCat = activeCategory.key === "Invoices";
            const subKey = `${activeCategory.key}::${sub.name}`;
            const subOpen = isInvoicesCat ? openSub === subKey : true;
            return (
              <div
                key={subKey}
                data-testid={`daily-cat-sub-${date}-${activeCategory.key}-${sub.name}`}
                className="rounded-md border border-slate-200 bg-white overflow-hidden"
              >
                {isInvoicesCat && (
                  <button
                    onClick={() => setOpenSub(subOpen ? null : subKey)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50"
                  >
                    <span className="text-xs font-bold text-slate-800 truncate">
                      📁 {sub.name === "UNKNOWN" ? "(unknown supplier)" : sub.name}
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono">
                      {sub.count} · €{sub.total.toFixed(2)}
                    </span>
                  </button>
                )}
                {subOpen && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid={`daily-table-${date}-${activeCategory.key}-${sub.name}`}>
                      <SpecTableHead />
                      <tbody>
                        {sub.rows.map((inv) => (
                          <DailyFolderRow
                            key={inv.id}
                            inv={inv}
                            onChequeClick={onChequeClick}
                            onOpenInvoice={onOpenInvoice}
                            onDelete={onDelete}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function DailyFolderRow({ inv, onChequeClick, onOpenInvoice, onDelete }) {
  const isPaid = ["PAID_CASH", "PAID_VISA"].includes(inv.status);
  const isPending = ["UNPAID", "PENDING_CHEQUE"].includes(inv.status);
  const dateCell = inv.cheque_maturity || inv.issue_date || "—";
  const hasCheque = !!inv.cheque_image_url;
  const kind = hasCheque ? "cheque" : "invoice";
  return (
    <tr
      data-testid={`daily-row-${inv.id}`}
      className="border-t border-slate-100 hover:bg-amber-50/40 transition-colors"
    >
      <td className="px-3 py-2">
        <button
          onClick={() => onOpenInvoice && onOpenInvoice(inv)}
          className="font-mono text-xs font-semibold text-blue-700 hover:underline"
          data-testid={`daily-row-invno-${inv.id}`}
          title="Open invoice"
        >
          {inv.invoice_number || "(no #)"}
        </button>
      </td>
      <td className="px-3 py-2 text-slate-800 font-medium truncate max-w-[180px]" title={inv.supplier_name}>
        {inv.supplier_name || "—"}
      </td>
      <td className="px-3 py-2 text-center">
        {inv.cheque_image_url ? (
          <button
            onClick={() => onChequeClick(inv.cheque_image_url)}
            data-testid={`daily-row-cheque-${inv.id}`}
            className="inline-block rounded-md ring-1 ring-slate-200 hover:ring-amber-400 transition-all overflow-hidden"
            title="View cheque photo"
          >
            <img
              src={fileUrl(inv.cheque_image_url)}
              alt=""
              className="h-9 w-12 object-cover"
            />
          </button>
        ) : (
          <span className="text-slate-300 text-[10px]">—</span>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-slate-700">{dateCell}</td>
      <td className="px-3 py-2 font-mono font-bold text-right text-slate-900">
        €{Number(inv.total_amount || 0).toFixed(2)}
      </td>
      <td className="px-3 py-2 text-center">
        <div className="inline-flex items-center gap-1.5">
          {isPaid ? (
            <span
              data-testid={`daily-row-status-${inv.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300 px-2 py-0.5 text-[10px] font-bold uppercase"
            >
              🟢 GREEN / PAID
            </span>
          ) : isPending ? (
            <span
              data-testid={`daily-row-status-${inv.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 ring-1 ring-rose-300 px-2 py-0.5 text-[10px] font-bold uppercase"
            >
              🔴 RED / PENDING
            </span>
          ) : (
            <span
              data-testid={`daily-row-status-${inv.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-300 px-2 py-0.5 text-[10px] font-bold uppercase"
            >
              ⚪ {inv.status}
            </span>
          )}
          {/* Iter 114 · Row-level delete of individual file (invoice / cheque / photo).
              Cascades opex + cheque + master-calendar via DELETE /invoices/{id}. */}
          {typeof onDelete === "function" && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(inv, kind); }}
              data-testid={`daily-row-delete-${inv.id}`}
              title={hasCheque
                ? "Διαγραφή επιταγής · Delete cheque"
                : "Διαγραφή τιμολογίου · Delete invoice"}
              className="inline-flex items-center rounded-md border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-700 hover:bg-rose-100"
            >
              🗑️
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}


function SuppliersGrid() {
  return (
    <section className="space-y-5">
      {CATEGORIES.map((cat) => {
        const items = SUPPLIERS.filter((s) => s.category === cat);
        return (
          <div key={cat} data-testid={`category-${cat.replace(/\W+/g, "-").toLowerCase()}`}>
            <h3 className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
              {cat} · <span className="text-slate-400">{items.length}</span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
              {items.map((s) => (
                <a
                  key={s.name}
                  href={waLink(s.phone)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`supplier-${s.name.replace(/\W+/g, "-").toLowerCase()}`}
                  className="bg-white border border-slate-200 rounded-xl p-2.5 hover:shadow-md hover:border-slate-400 transition-all flex flex-col justify-between min-h-[92px]"
                >
                  <div className="font-semibold text-[12px] text-slate-900 leading-tight line-clamp-2">{s.name}</div>
                  <div className="mt-1.5 flex items-center gap-1 text-[10px] font-mono text-emerald-700">
                    <Phone className="h-3 w-3" />
                    {s.phone}
                  </div>
                </a>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

// ----------------- RIGHT COLUMN: PRICING CALCULATOR -----------------
function PricingCalculator({ shift }) {
  const [productName, setProductName] = useState("");
  const [barcode, setBarcode] = useState("");
  const [category, setCategory] = useState("");
  const [costStr, setCostStr] = useState("");
  const [vatRate, setVatRate] = useState(19);
  const [margin, setMargin] = useState(35);
  const [marginTouched, setMarginTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);

  const cost = Number(costStr) || 0;

  // Auto-suggest margin from category (only if user hasn't manually adjusted it yet)
  const pickCategory = (cat) => {
    setCategory(cat);
    const suggested = CATEGORY_DEFAULT_MARGIN[cat];
    if (!marginTouched && suggested != null) {
      setMargin(suggested);
    }
  };
  const setMarginManual = (v) => {
    setMargin(v);
    setMarginTouched(true);
  };

  const { costIncVat, retail, netProfit } = useMemo(() => {
    const vat = vatRate / 100;
    const m = margin / 100;
    if (cost <= 0 || m >= 1) {
      return { costIncVat: 0, retail: 0, netProfit: 0 };
    }
    const costInc = cost * (1 + vat);
    const r = (cost / (1 - m)) * (1 + vat);
    const np = r / (1 + vat) - cost;
    return { costIncVat: costInc, retail: r, netProfit: np };
  }, [cost, vatRate, margin]);

  const loadItems = async () => {
    if (!shift) {
      setItems([]);
      return;
    }
    try {
      const { data } = await api.get("/shift-invoices", { params: { shift_id: shift.id } });
      setItems(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadItems();
  }, [shift?.id]);

  const addToShift = async () => {
    if (!shift) {
      toast.error("Start a shift first (Tab 1)");
      return;
    }
    if (!productName.trim()) {
      toast.error("Product name required");
      return;
    }
    if (cost <= 0) {
      toast.error("Cost must be > 0");
      return;
    }
    setSaving(true);
    try {
      await api.post("/shift-invoices", {
        shift_id: shift.id,
        location: shift.location,
        product_name: productName.trim(),
        barcode: barcode.trim() || null,
        category: category || null,
        cost_ex_vat: cost,
        vat_rate: vatRate,
        margin_pct: margin,
      });
      toast.success(`${productName.trim()} added to shift`);
      setProductName("");
      setCostStr("");
      setBarcode("");
      setMarginTouched(false);
      loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onBarcodeDetected = async (code) => {
    setBarcode(code);
    setScannerOpen(false);
    if (!shift) {
      toast.info(`Barcode ${code} captured — start a shift to look it up`);
      return;
    }
    try {
      const { data } = await api.get("/shift-invoices/lookup", {
        params: { query: code, location: shift.location },
      });
      // Pre-fill the calculator from the most recent record
      setProductName(data.product_name || "");
      setCategory(data.category || "");
      setCostStr(String(data.cost_ex_vat));
      setVatRate(data.vat_rate);
      setMargin(data.margin_pct);
      setMarginTouched(true);
      toast.success(`Found: ${data.product_name} (last cost €${data.cost_ex_vat.toFixed(2)})`);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.info("New barcode — enter product details to add it");
      } else {
        toast.error("Lookup failed");
      }
    }
  };

  const removeItem = async (id) => {
    try {
      await api.delete(`/shift-invoices/${id}`);
      loadItems();
    } catch {
      toast.error("Delete failed");
    }
  };

  const updatePos = async (id, posValue) => {
    try {
      const payload = { pos_price: posValue === "" || posValue == null ? null : Number(posValue) };
      const { data } = await api.patch(`/shift-invoices/${id}`, payload);
      setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
    } catch {
      toast.error("Save POS price failed");
    }
  };

  // Iter 27 — Live inline override. The row paints itself optimistically
  // via `optimistic` (immediate header math reflection), then the debounced
  // PATCH lands and we reconcile against the server-rounded numbers.
  const applyOptimistic = (id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };
  const updateRow = async (id, payload) => {
    try {
      const { data } = await api.patch(`/shift-invoices/${id}`, payload);
      setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
    } catch {
      toast.error("Save override failed");
    }
  };

  // Iter 28 — Apply a supplier-level discount across every row that
  // shares the same OCR batch (bulk_batch_id). One round-trip; the
  // backend recomputes Net Cost + Retail + Profit per row and we paint
  // the whole batch optimistically before reconciling.
  const cascadeBatchDiscount = async (batchId, discAmt, discPct) => {
    if (!batchId) return;
    // Optimistic paint — push the new discount + provisional net cost into
    // every row of this batch so the header counter shifts instantly.
    setItems((prev) =>
      prev.map((i) => {
        if (i.bulk_batch_id !== batchId) return i;
        const raw = i.raw_cost_ex_vat != null ? i.raw_cost_ex_vat : i.cost_ex_vat;
        const netCost = Math.max(
          0,
          raw * (1 - (Number(discPct) || 0) / 100) - (Number(discAmt) || 0),
        );
        const vat = (i.vat_rate || 0) / 100;
        const margin = (i.margin_pct || 0) / 100;
        const retail = margin < 1 ? (netCost / (1 - margin)) * (1 + vat) : 0;
        const profit = (1 + vat) > 0 ? retail / (1 + vat) - netCost : 0;
        return {
          ...i,
          raw_cost_ex_vat: raw,
          line_discount_pct: Number(discPct) || 0,
          line_discount_amount: Number(discAmt) || 0,
          cost_ex_vat: netCost,
          cost_inc_vat: netCost * (1 + vat),
          retail_price: retail,
          net_profit: profit,
        };
      }),
    );
    try {
      const { data } = await api.post("/shift-invoices/batch-discount", {
        bulk_batch_id: batchId,
        line_discount_amount: Number(discAmt) || 0,
        line_discount_pct: Number(discPct) || 0,
      });
      // Reconcile from server-rounded numbers.
      const byId = Object.fromEntries((data.items || []).map((r) => [r.id, r]));
      setItems((prev) => prev.map((i) => (byId[i.id] ? byId[i.id] : i)));
      toast.success(`Discount cascaded to ${data.count} line(s)`);
    } catch (e) {
      toast.error("Batch cascade failed — re-syncing");
      loadItems();
    }
  };

  const downloadExport = async (fmt) => {
    if (!shift) return;
    try {
      const res = await api.get(`/shift-invoices/export/${fmt}`, {
        params: { shift_id: shift.id },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shift-${shift.location}-r${shift.register}-${shift.started_at.slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const printAllLabels = async () => {
    if (!shift) return;
    setPrintBusy(true);
    try {
      const res = await api.get(`/shift-invoices/labels`, {
        params: { shift_id: shift.id },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank", "noopener");
      if (!win) {
        // Pop-up blocked — fall back to direct download
        const a = document.createElement("a");
        a.href = url;
        a.download = `labels-${shift.location}-r${shift.register}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.info("Labels downloaded — open the PDF and print to your thermal printer");
      } else {
        // Trigger native print dialog once PDF is loaded
        try {
          win.addEventListener("load", () => {
            setTimeout(() => { try { win.print(); } catch { /* ignore */ } }, 500);
          });
        } catch { /* ignore */ }
        toast.success(`${items.length} labels sent to printer dialog`);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "No items to print" : "Print failed");
    } finally {
      setPrintBusy(false);
    }
  };

  // ----- bulk OCR -----
  const bulkCamRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [lastBatch, setLastBatch] = useState(null); // { count, image_url, ids:[] }

  // Iter 31 — Kronos AI-Audit Reconciliation. Operator photographs the
  // weekly Kronos Z-tape; backend OCRs the brand totals and cross-checks
  // them against the trailing 7-day Kronos invoice ledger. On mismatch
  // we freeze the screen with a bilingual alert modal showing the
  // row-by-row delta breakdown.
  // Iter 32 — multi-page capture loop. The first photo opens a "next
  // page?" dialog; operator can keep scanning until they tap Finalize,
  // then we POST the full array to /api/kronos/audit-z-report.
  const kronosCamRef = useRef(null);
  const [kronosBusy, setKronosBusy] = useState(false);
  const [kronosAudit, setKronosAudit] = useState(null); // { matches, breakdown[], ... }
  const [kronosPages, setKronosPages] = useState([]); // base64 strings
  const [kronosNextPagePrompt, setKronosNextPagePrompt] = useState(false);
  const [kronosCommitToShift, setKronosCommitToShift] = useState(true);

  const handleBulkScan = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!shift) {
      toast.error("Start a shift first (Tab 1)");
      return;
    }
    setBulkBusy(true);
    const t = toast.loading("Scanning invoice line-by-line…");
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/ocr/bulk-products", {
        image_base64: b64,
        shift_id: shift.id,
        location: shift.location,
        margin_pct: margin,
      });
      if (!data.count) {
        toast.error("No products detected in this invoice", { id: t });
      } else {
        toast.success(`${data.count} product(s) extracted at ${margin}% margin`, { id: t });
      }
      setLastBatch({
        batch_id: data.batch_id,
        count: data.count,
        image_url: data.image_url,
        ids: (data.items || []).map((i) => i.id),
      });
      loadItems();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Bulk scan failed", { id: t });
    } finally {
      setBulkBusy(false);
    }
  };

  // Iter 32 — Multi-page Kronos audit capture loop.
  // 1) Operator clicks the Kronos Z-Audit button → camera fires.
  // 2) After EACH photo we stash the b64 and open the "next page?" dialog.
  // 3) "Yes" → re-trigger the camera; "No, Finalize" → submit the array.
  const handleKronosPageCapture = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setKronosPages((prev) => [...prev, b64]);
      setKronosNextPagePrompt(true);
    } catch {
      toast.error("Could not read page image");
    }
  };

  const askForNextKronosPage = () => {
    setKronosNextPagePrompt(false);
    // Defer the click so the dialog has time to close cleanly.
    setTimeout(() => kronosCamRef.current?.click(), 80);
  };

  const finalizeKronosAudit = async () => {
    setKronosNextPagePrompt(false);
    const pages = kronosPages;
    if (!pages.length) return;
    setKronosBusy(true);
    const tid = toast.loading(`AI reading Kronos Z-Report (${pages.length} page${pages.length > 1 ? "s" : ""})…`);
    try {
      const { data } = await api.post("/kronos/audit-z-report", {
        image_base64s: pages,
        shift_id: shift?.id || null,
        window_days: 7,
        commit_to_shift: kronosCommitToShift && !!shift,
      });
      if (data.matches) {
        toast.success("Kronos Z reconciled · all streams match", { id: tid });
      } else {
        toast.error("Kronos Z mismatch detected", { id: tid });
      }
      setKronosAudit(data);
      // If we auto-committed shift rows, refresh the active shift list
      if (data.committed_count) {
        loadItems();
        toast.success(`${data.committed_count} Kronos line(s) added to shift`, { duration: 4000 });
      }
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Kronos audit failed", { id: tid });
    } finally {
      setKronosBusy(false);
      setKronosPages([]);
    }
  };

  const cancelKronosAudit = () => {
    setKronosNextPagePrompt(false);
    setKronosPages([]);
    toast.info("Kronos scan cancelled");
  };

  const shiftTotal = items.reduce((s, i) => s + i.net_profit, 0);
  const mismatches = items.filter(
    (i) => typeof i.pos_price === "number" && Math.abs(i.pos_price - i.retail_price) >= 0.05
  ).length;

  return (
    <Card
      className="p-5 bg-white border-slate-200 shadow-sm"
      data-testid="pricing-calculator-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
          <Calculator className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900 leading-tight">Pricing Calculator & Profit Analyst</h2>
          <p className="text-[11px] text-slate-500 leading-tight">Υπολογιστής Τιμής & Καθαρού Κέρδους</p>
        </div>
      </div>

      <div className="space-y-4 mt-4">
        {/* Barcode + scan */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Barcode (optional) · Γραμμωτός Κώδικας
          </Label>
          <div className="mt-1.5 flex gap-2">
            <Input
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder="EAN / UPC / Code-128"
              data-testid="calc-barcode-input"
              className="flex-1 min-h-[48px] font-mono"
            />
            <Button
              onClick={() => setScannerOpen(true)}
              variant="outline"
              data-testid="calc-barcode-scan-btn"
              className="min-h-[48px] gap-1 border-2"
              title="Scan a barcode with the tablet camera"
            >
              <Barcode className="h-4 w-4" />
              SCAN
            </Button>
          </div>
        </div>

        {/* Product name */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Product Name · Προϊόν
          </Label>
          <Input
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="e.g. Marlboro Red 20s"
            data-testid="calc-product-name"
            className="mt-1.5 min-h-[48px] font-semibold"
          />
        </div>

        {/* Category dropdown (auto-suggests margin) */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Category · Κατηγορία
            </Label>
            {category && CATEGORY_DEFAULT_MARGIN[category] != null && (
              <span className="text-[10px] font-mono text-emerald-700">
                default margin {CATEGORY_DEFAULT_MARGIN[category]}%
              </span>
            )}
          </div>
          <Select value={category} onValueChange={pickCategory}>
            <SelectTrigger className="mt-1.5 min-h-[48px]" data-testid="calc-category">
              <SelectValue placeholder="(optional) pick a category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`calc-category-opt-${c.replace(/\W+/g, "-").toLowerCase()}`}>
                  {c} · {CATEGORY_DEFAULT_MARGIN[c]}%
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Cost price */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Cost Price Excluding VAT · Τιμή Χωρίς ΦΠΑ (€)
          </Label>
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={costStr}
            onChange={(e) => setCostStr(e.target.value)}
            placeholder="0.00"
            data-testid="calc-cost-ex-vat"
            className="mt-1.5 min-h-[56px] text-2xl font-mono font-bold text-center border-2"
          />
        </div>

        {/* VAT radio chips */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            VAT Rate · Συντελεστής ΦΠΑ
          </Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5" role="radiogroup" data-testid="calc-vat-group">
            {VAT_RATES.map((r) => (
              <button
                key={r}
                onClick={() => setVatRate(r)}
                role="radio"
                aria-checked={vatRate === r}
                data-testid={`calc-vat-${r}`}
                className={`min-h-[52px] rounded-xl border-2 font-bold text-base transition-colors ${
                  vatRate === r
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-900 hover:border-slate-400"
                }`}
              >
                {r}%
              </button>
            ))}
          </div>
        </div>

        {/* Margin slider */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Profit Margin · Ποσοστό Κέρδους
            </Label>
            <span
              data-testid="calc-margin-value"
              className="font-mono font-bold text-lg text-slate-900"
            >
              {margin}%
            </span>
          </div>
          <Slider
            value={[margin]}
            min={0}
            max={90}
            step={1}
            onValueChange={(v) => setMarginManual(v[0])}
            data-testid="calc-margin-slider"
            className="mt-3"
          />
          <Input
            type="number"
            min="0"
            max="95"
            value={margin}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (Number.isFinite(v)) setMarginManual(Math.max(0, Math.min(95, v)));
            }}
            data-testid="calc-margin-input"
            className="mt-2 min-h-[44px] text-center font-mono font-bold"
          />
        </div>
      </div>

      {/* Cost inc VAT row */}
      <div
        className="mt-5 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
        data-testid="calc-cost-inc-vat-row"
      >
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          Cost Inc. VAT · Τιμή Με ΦΠΑ
        </div>
        <div className="font-mono font-bold text-slate-900 text-base" data-testid="calc-cost-inc-vat">
          €{costIncVat.toFixed(2)}
        </div>
      </div>

      {/* Final outputs */}
      <div className="mt-3 space-y-2">
        <ResultCard
          tone="blue"
          label="ΤΕΛΙΚΗ ΤΙΜΗ ΡΑΦΙΟΥ (ΜΕ ΦΠΑ)"
          subLabel="Final Shelf Price · Incl. VAT"
          value={`€${retail.toFixed(2)}`}
          testId="calc-result-retail"
          icon={<Tag className="h-5 w-5" />}
        />
        <ResultCard
          tone="green"
          label="ΚΑΘΑΡΟ ΚΕΡΔΟΣ ΑΝΑ ΤΕΜΑΧΙΟ"
          subLabel="Net Profit per Unit"
          value={`€${netProfit.toFixed(2)}`}
          testId="calc-result-profit"
          icon={<TrendingUp className="h-5 w-5" />}
        />
      </div>

      <Button
        onClick={addToShift}
        disabled={!shift || saving || cost <= 0 || !productName.trim()}
        data-testid="calc-add-to-shift"
        className="mt-4 w-full min-h-[60px] bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-widest gap-2"
      >
        <Plus className="h-5 w-5" />
        {saving ? "Saving…" : "Add to Shift"}
      </Button>
      {!shift && (
        <p className="text-[11px] text-slate-500 mt-2 text-center">
          Start a shift on Tab 1 to enable Add-to-Shift.
        </p>
      )}

      {/* --- BULK INVOICE AI SCAN --- */}
      <div className="mt-5 pt-5 border-t border-dashed border-slate-300">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-amber-500" />
          <h3 className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">
            AI Bulk Product Scanner · Σαρωτής Τιμολογίου
          </h3>
        </div>
        <p className="text-[11px] text-slate-500 leading-snug mb-3">
          Photograph an entire supplier invoice. AI reads every row, detects each product&apos;s
          cost &amp; VAT bracket, and applies the current <b>{margin}%</b> margin to all of them.
        </p>
        <input
          ref={bulkCamRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleBulkScan}
          className="hidden"
          data-testid="bulk-scan-input"
        />
        <Button
          onClick={() => bulkCamRef.current?.click()}
          disabled={!shift || bulkBusy}
          data-testid="bulk-scan-btn"
          className="w-full min-h-[68px] bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold uppercase tracking-widest gap-2 text-base"
        >
          <ScanLine className="h-5 w-5" />
          {bulkBusy ? "AI READING INVOICE…" : "BULK SCAN INVOICE"}
        </Button>

        {/* Iter 31/32 — Kronos AI-Audit multi-page camera trigger. The
            same input fires for every page; the dialog below decides
            whether to fire it again or finalize the scan. */}
        <input
          ref={kronosCamRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleKronosPageCapture}
          className="hidden"
          data-testid="kronos-audit-input"
        />
        <Button
          onClick={() => {
            setKronosPages([]);
            kronosCamRef.current?.click();
          }}
          disabled={kronosBusy}
          data-testid="kronos-audit-btn"
          className="mt-2 w-full min-h-[52px] bg-gradient-to-r from-violet-700 to-fuchsia-700 hover:from-violet-800 hover:to-fuchsia-800 text-white font-bold uppercase tracking-widest gap-2"
        >
          <ShieldCheck className="h-4 w-4" />
          {kronosBusy ? "AI auditing Kronos Z…" : "Kronos Z-Audit · Έλεγχος"}
        </Button>
        {shift && (
          <label
            className="mt-2 flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer select-none"
            data-testid="kronos-commit-toggle-label"
          >
            <input
              type="checkbox"
              checked={kronosCommitToShift}
              onChange={(e) => setKronosCommitToShift(e.target.checked)}
              className="h-3.5 w-3.5 accent-violet-700"
              data-testid="kronos-commit-toggle"
            />
            <span>
              <b>+ ADD TO SHIFT</b> · auto-commit each Kronos sub-brand total
              into the active shift after audit.
            </span>
          </label>
        )}
        {lastBatch && (
          <div
            className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
            data-testid="bulk-last-batch"
          >
            {lastBatch.image_url && (
              <img
                src={fileUrl(lastBatch.image_url)}
                alt="last scan"
                className="h-12 w-12 object-cover rounded border"
              />
            )}
            <div className="flex-1 text-xs text-amber-900">
              Last scan extracted <b>{lastBatch.count}</b> product(s) at {margin}% margin.
            </div>
          </div>
        )}
      </div>

      {/* SHIFT INVOICES LOG */}
      <div className="mt-6 pt-5 border-t border-slate-200" data-testid="shift-invoices-log">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
            <ReceiptText className="h-3.5 w-3.5" />
            Shift Invoices · Καταχωρήσεις Βάρδιας
          </h3>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => setCreditOpen(true)}
              disabled={!shift}
              data-testid="open-credit-note-btn"
              className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-orange-100 hover:bg-orange-200 text-orange-700 disabled:opacity-40 flex items-center gap-1"
              title="Add Credit Note / Returns"
            >
              <RefreshCw className="h-3 w-3" />
              CREDIT
            </button>
            <button
              onClick={printAllLabels}
              disabled={!shift || items.length === 0 || printBusy}
              data-testid="print-labels-btn"
              className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-blue-100 hover:bg-blue-200 text-blue-700 disabled:opacity-40 flex items-center gap-1"
              title="Print all shelf labels to Bluetooth / OS printer"
            >
              <Printer className="h-3 w-3" />
              {printBusy ? "PRINT…" : "LABELS"}
            </button>
            <button
              onClick={() => downloadExport("csv")}
              disabled={!shift || items.length === 0}
              data-testid="export-csv-btn"
              className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-40 flex items-center gap-1"
              title="Export CSV"
            >
              <Download className="h-3 w-3" />
              CSV
            </button>
            <button
              onClick={() => downloadExport("pdf")}
              disabled={!shift || items.length === 0}
              data-testid="export-pdf-btn"
              className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-700 disabled:opacity-40 flex items-center gap-1"
              title="Export PDF"
            >
              <FileText className="h-3 w-3" />
              PDF
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between mb-3 text-[11px] font-mono">
          <span className="text-slate-500">
            {items.length} item(s) · Profit €{shiftTotal.toFixed(2)}
          </span>
          {mismatches > 0 && (
            <span className="text-rose-700 flex items-center gap-1" data-testid="mismatch-summary">
              <AlertTriangle className="h-3 w-3" />
              {mismatches} mismatch(es)
            </span>
          )}
        </div>
        {items.length === 0 ? (
          <div
            className="px-3 py-4 text-center text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-lg"
            data-testid="shift-invoices-empty"
          >
            No products added this shift yet.
          </div>
        ) : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
            {items.map((it) => (
              <ShiftInvoiceRow
                key={it.id}
                item={it}
                onPosChange={updatePos}
                onDelete={removeItem}
                onOptimistic={applyOptimistic}
                onCommitOverride={updateRow}
                onCascadeBatch={cascadeBatchDiscount}
              />
            ))}
          </div>
        )}
      </div>

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={onBarcodeDetected}
      />
      <CreditNoteDialog
        open={creditOpen}
        onClose={() => setCreditOpen(false)}
        shift={shift}
        onCreated={() => { /* nothing else to refresh here */ }}
      />

      {/* Iter 32 — Multi-page capture prompt. After each photo we ask
          the operator whether the document has more pages. Yes → next
          shot; No, Finalize → POST the array to the audit endpoint. */}
      <Dialog
        open={kronosNextPagePrompt}
        onOpenChange={(o) => { if (!o) cancelKronosAudit(); }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="kronos-nextpage-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ScanLine className="h-6 w-6 text-violet-700" />
              <span>Multi-Page Kronos Scan</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p
              className="text-sm text-slate-700 leading-relaxed"
              data-testid="kronos-nextpage-prompt"
            >
              📸 <b>Υπάρχει επόμενη σελίδα; / Is there another page?</b>
              <br />
              <span className="text-[12px] text-slate-500">
                Pages captured so far: <b data-testid="kronos-pages-count">{kronosPages.length}</b>
              </span>
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={askForNextKronosPage}
                data-testid="kronos-next-page-btn"
                className="w-full min-h-[48px] bg-violet-700 hover:bg-violet-800 text-white font-bold uppercase tracking-widest"
              >
                Ναι · Yes, scan next page
              </Button>
              <Button
                onClick={finalizeKronosAudit}
                data-testid="kronos-finalize-btn"
                className="w-full min-h-[48px] bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-widest"
              >
                <Check className="h-4 w-4" />
                Όχι, ολοκλήρωση · No, Finalize Scanning
              </Button>
              <Button
                variant="outline"
                onClick={cancelKronosAudit}
                data-testid="kronos-cancel-btn"
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter 31 — Kronos AI-Audit Reconciliation modal. Renders on any
          Z-Audit response (match OR mismatch). Mismatch path freezes the
          flow with a bold bilingual banner + row-by-row delta breakdown. */}
      <Dialog
        open={!!kronosAudit}
        onOpenChange={(o) => { if (!o) setKronosAudit(null); }}
      >
        <DialogContent
          className={`max-w-2xl ${kronosAudit?.matches ? "" : "border-4 border-rose-600 shadow-rose-300 shadow-2xl"}`}
          data-testid="kronos-audit-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tight flex items-center gap-2">
              {kronosAudit?.matches ? (
                <>
                  <Check className="h-7 w-7 text-emerald-600" />
                  <span className="text-emerald-700">
                    Kronos Z Reconciled · Συμφωνία
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-7 w-7 text-rose-600 animate-pulse" />
                  <span className="text-rose-700">
                    🚨 AI AUDIT ALERT · ΑΠΟΚΛΙΣΗ KRONOS Z!
                  </span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {kronosAudit && (
            <div className="space-y-4" data-testid="kronos-audit-body">
              <p className="text-sm text-slate-600">
                Trailing <b>{kronosAudit.window_days}-day</b> Kronos invoice ledger vs.
                scanned Z-Report ({kronosAudit.report_date || "today"}).
                {kronosAudit.pages_count > 1 && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded"
                    data-testid="kronos-audit-pages-badge"
                  >
                    <ScanLine className="h-3 w-3" />
                    {kronosAudit.pages_count} pages
                  </span>
                )}
                {kronosAudit.committed_count > 0 && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded"
                    data-testid="kronos-audit-committed-badge"
                  >
                    <Check className="h-3 w-3" />
                    +{kronosAudit.committed_count} lines → shift
                  </span>
                )}
              </p>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full text-sm" data-testid="kronos-audit-table">
                  <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Stream</th>
                      <th className="text-right px-3 py-2">Z-Report</th>
                      <th className="text-right px-3 py-2">System</th>
                      <th className="text-right px-3 py-2">Δ Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kronosAudit.breakdown.map((r) => (
                      <tr
                        key={r.key}
                        data-testid={`kronos-audit-row-${r.key}`}
                        className={
                          r.key === "grand"
                            ? "bg-slate-50 font-bold border-t-2 border-slate-300"
                            : r.matches
                            ? "bg-white"
                            : "bg-rose-50"
                        }
                      >
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {r.label}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          €{r.z_report_total.toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          €{r.system_total.toFixed(2)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono font-bold ${
                            r.matches
                              ? "text-emerald-700"
                              : r.delta > 0
                              ? "text-amber-700"
                              : "text-rose-700"
                          }`}
                        >
                          {r.matches ? (
                            "✓"
                          ) : (
                            <>
                              {r.delta >= 0 ? "+" : ""}
                              €{r.delta.toFixed(2)}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!kronosAudit.matches && (
                <div
                  className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-900"
                  data-testid="kronos-audit-hint"
                >
                  <b>Investigate / Έλεγχος:</b> compare each highlighted row against the
                  invoices archived this week under{" "}
                  <span className="font-mono">/storage/invoices/Kronos/…</span>.
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setKronosAudit(null)}
                  data-testid="kronos-audit-close"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ShiftInvoiceRow({ item, onPosChange, onDelete, onOptimistic, onCommitOverride, onCascadeBatch }) {
  const [posInput, setPosInput] = useState(
    item.pos_price == null ? "" : String(item.pos_price)
  );
  // Iter 27 — Inline override drafts. Cost, Discount and Retail are live
  // editable. Net Cost = Raw - Discount; Profit = Net Retail - Net Cost.
  // Every keystroke pushes an optimistic patch into parent state so the
  // header counter / Profit aggregate refresh live with no refresh.
  const rawCostInit =
    item.raw_cost_ex_vat != null ? item.raw_cost_ex_vat : item.cost_ex_vat;
  const [costInput, setCostInput] = useState(
    rawCostInit == null ? "" : String(rawCostInit),
  );
  const [discInput, setDiscInput] = useState(
    item.line_discount_amount == null || item.line_discount_amount === 0
      ? ""
      : String(item.line_discount_amount),
  );
  const [retailInput, setRetailInput] = useState(
    item.retail_price == null ? "" : String(item.retail_price),
  );
  const debounceRef = useRef(null);

  // Keep local drafts in sync when the row is replaced (e.g. after PATCH).
  useEffect(() => {
    setCostInput(rawCostInit == null ? "" : String(rawCostInit));
    setDiscInput(
      item.line_discount_amount == null || item.line_discount_amount === 0
        ? ""
        : String(item.line_discount_amount),
    );
    setRetailInput(item.retail_price == null ? "" : String(item.retail_price));
  }, [item.id, item.raw_cost_ex_vat, item.cost_ex_vat, item.line_discount_amount, item.retail_price]);

  const pos = posInput === "" ? null : Number(posInput);
  const delta = typeof pos === "number" && Number.isFinite(pos) ? pos - item.retail_price : null;
  const mismatched = delta !== null && Math.abs(delta) >= 0.05;
  const isBulk = item.source === "bulk_ocr";

  const commitPos = () => {
    const newVal = posInput === "" ? null : Number(posInput);
    const oldVal = item.pos_price == null ? null : Number(item.pos_price);
    if (newVal === oldVal) return;
    if (newVal !== null && !Number.isFinite(newVal)) return;
    onPosChange(item.id, posInput);
  };

  // ----- Live override math hook -----
  const num = (s) => {
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };
  const recalcAndPaint = (nextCost, nextDisc, nextRetail) => {
    const rawCost = num(nextCost);
    const discAmt = Math.max(0, num(nextDisc));
    const netCost = Math.max(0, rawCost - discAmt);
    const vat = (item.vat_rate || 0) / 100;
    const margin = (item.margin_pct || 0) / 100;
    // If retail is blank/0 fall back to the margin-derived shelf price.
    const r = num(nextRetail);
    const retail = r > 0
      ? r
      : (margin < 1 ? (netCost / (1 - margin)) * (1 + vat) : 0);
    const profit = (1 + vat) > 0 ? retail / (1 + vat) - netCost : 0;
    onOptimistic(item.id, {
      raw_cost_ex_vat: rawCost,
      line_discount_amount: discAmt,
      cost_ex_vat: netCost,
      cost_inc_vat: netCost * (1 + vat),
      retail_price: retail,
      net_profit: profit,
    });
  };

  const queueCommit = (patch) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onCommitOverride(item.id, patch);
    }, 600);
  };

  const onCostChange = (v) => {
    setCostInput(v);
    recalcAndPaint(v, discInput, retailInput);
    queueCommit({
      raw_cost_ex_vat: num(v),
      line_discount_amount: Math.max(0, num(discInput)),
    });
  };
  const onDiscChange = (v) => {
    setDiscInput(v);
    recalcAndPaint(costInput, v, retailInput);
    queueCommit({
      raw_cost_ex_vat: num(costInput),
      line_discount_amount: Math.max(0, num(v)),
    });
  };
  const onRetailChange = (v) => {
    setRetailInput(v);
    recalcAndPaint(costInput, discInput, v);
    // Sending retail_price tells the backend to lock to this explicit value
    // instead of recomputing from margin.
    queueCommit({ retail_price: num(v) });
  };

  // Live derived values for THIS row (mirror what onOptimistic just pushed).
  const liveNetCost = item.cost_ex_vat || 0;
  const liveRetail = item.retail_price || 0;
  const liveProfit = item.net_profit || 0;
  const hasDiscount = (item.line_discount_amount || 0) > 0 || (item.line_discount_pct || 0) > 0;

  return (
    <div
      data-testid={`shift-invoice-${item.id}`}
      className={`border rounded-lg px-3 py-2 ${
        mismatched
          ? "border-rose-300 bg-rose-50"
          : isBulk
          ? "border-amber-200 bg-amber-50/40"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      {/* Structured line in spec format: [Name] | Cost | VAT | Margin | -> RETAIL */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-0 flex items-center gap-1.5">
          {isBulk && (
            <span
              title="Extracted by AI from bulk invoice scan"
              className="inline-flex items-center gap-0.5 text-[9px] font-bold tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
            >
              <Sparkles className="h-2.5 w-2.5" />
              AI
            </span>
          )}
          {typeof item.cost_delta_pct === "number" && item.cost_delta_pct > 0 && (
            <span
              title={`Cost increased from €${(item.prev_cost ?? 0).toFixed(2)}`}
              data-testid={`cost-up-${item.id}`}
              className="inline-flex items-center gap-0.5 text-[9px] font-bold tracking-widest text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded"
            >
              <AlertTriangle className="h-2.5 w-2.5" />
              +{item.cost_delta_pct.toFixed(1)}%
            </span>
          )}
          {typeof item.cost_delta_pct === "number" && item.cost_delta_pct < 0 && (
            <span
              title={`Cost decreased from €${(item.prev_cost ?? 0).toFixed(2)}`}
              data-testid={`cost-down-${item.id}`}
              className="inline-flex items-center gap-0.5 text-[9px] font-bold tracking-widest text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded"
            >
              <TrendingDown className="h-2.5 w-2.5" />
              {item.cost_delta_pct.toFixed(1)}%
            </span>
          )}
          <span
            className="font-semibold text-sm text-slate-900 truncate"
            data-testid={`shift-invoice-name-${item.id}`}
          >
            {item.product_name}
          </span>
          {item.barcode && (
            <span
              className="text-[9px] font-mono text-slate-400 truncate"
              title={`Barcode ${item.barcode}`}
              data-testid={`shift-invoice-bc-${item.id}`}
            >
              · {item.barcode}
            </span>
          )}
        </div>
        <button
          onClick={() => onDelete(item.id)}
          className="p-1 rounded-md hover:bg-red-100 text-slate-400 hover:text-red-600 shrink-0"
          data-testid={`shift-invoice-delete-${item.id}`}
          title="Remove"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Inline editable override row — Cost · Disc · Retail */}
      <div
        className="mt-2 grid grid-cols-3 gap-2"
        data-testid={`shift-invoice-edit-${item.id}`}
      >
        <div className="flex flex-col">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">
            Cost €
          </Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={costInput}
            onChange={(e) => onCostChange(e.target.value)}
            data-testid={`shift-invoice-cost-${item.id}`}
            className="h-7 text-xs font-mono text-center px-1 py-0"
          />
        </div>
        <div className="flex flex-col">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold flex items-center justify-between">
            <span>Disc €</span>
            {item.bulk_batch_id && (
              <button
                type="button"
                onClick={() =>
                  onCascadeBatch(
                    item.bulk_batch_id,
                    Number(discInput) || 0,
                    Number(item.line_discount_pct) || 0,
                  )
                }
                title="Apply this discount to every row in the same OCR batch"
                data-testid={`shift-invoice-cascade-${item.id}`}
                className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-widest text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-1 py-0.5 rounded transition-colors"
              >
                <Layers className="h-2.5 w-2.5" />
                ALL
              </button>
            )}
          </Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={discInput}
            onChange={(e) => onDiscChange(e.target.value)}
            placeholder="0.00"
            data-testid={`shift-invoice-disc-${item.id}`}
            className="h-7 text-xs font-mono text-center px-1 py-0"
          />
        </div>
        <div className="flex flex-col">
          <Label className="text-[9px] uppercase tracking-widest text-blue-700 font-bold">
            Retail €
          </Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={retailInput}
            onChange={(e) => onRetailChange(e.target.value)}
            data-testid={`shift-invoice-retail-input-${item.id}`}
            className="h-7 text-xs font-mono text-center px-1 py-0 font-bold text-blue-700"
          />
        </div>
      </div>

      {/* Live recap row — Net Cost · VAT · Margin → Retail · Profit */}
      <div
        className="flex items-center justify-between gap-1 mt-1 flex-wrap font-mono text-[11px]"
        data-testid={`shift-invoice-stats-${item.id}`}
      >
        <span className="text-slate-600">
          Net: <b className="text-slate-900">€{liveNetCost.toFixed(2)}</b>
          {hasDiscount && (
            <span
              className="ml-1 text-[9px] font-bold text-emerald-700"
              title="Discount applied to raw cost"
              data-testid={`shift-invoice-disc-tag-${item.id}`}
            >
              -€{(item.line_discount_amount || 0).toFixed(2)}
            </span>
          )}
        </span>
        <span className="text-slate-400">|</span>
        <span className="text-slate-600">
          VAT: <b className="text-slate-900">{item.vat_rate}%</b>
        </span>
        <span className="text-slate-400">|</span>
        <span className="text-slate-600">
          Margin: <b className="text-slate-900">{item.margin_pct}%</b>
        </span>
        <span className="text-slate-400">→</span>
        <span className="text-[10px] uppercase tracking-widest font-bold text-blue-700">
          Retail
        </span>
        <span
          className="font-mono font-bold text-blue-700 text-sm"
          data-testid={`shift-invoice-retail-${item.id}`}
        >
          €{liveRetail.toFixed(2)}
        </span>
        <span
          className="text-[11px] text-emerald-700 ml-auto font-bold"
          data-testid={`shift-invoice-profit-${item.id}`}
        >
          +€{liveProfit.toFixed(2)}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-bold whitespace-nowrap">
          POS €
        </Label>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={posInput}
          onChange={(e) => setPosInput(e.target.value)}
          onBlur={commitPos}
          onKeyDown={(e) => e.key === "Enter" && commitPos()}
          placeholder="—"
          data-testid={`shift-invoice-pos-${item.id}`}
          className="h-8 text-sm font-mono text-center px-2 py-0 w-24"
        />
        {delta !== null &&
          (mismatched ? (
            <span
              className="text-[10px] font-bold text-rose-700 flex items-center gap-1"
              data-testid={`shift-invoice-mismatch-${item.id}`}
            >
              <AlertTriangle className="h-3 w-3" />
              Δ {delta >= 0 ? "+" : ""}€{delta.toFixed(2)}
            </span>
          ) : (
            <span
              className="text-[10px] font-bold text-emerald-700 flex items-center gap-1"
              data-testid={`shift-invoice-match-${item.id}`}
            >
              <Check className="h-3 w-3" />
              match
            </span>
          ))}
      </div>
    </div>
  );
}

function ResultCard({ tone, label, subLabel, value, testId, icon }) {
  const styles =
    tone === "blue"
      ? "bg-blue-50 border-blue-200 text-blue-900"
      : "bg-emerald-50 border-emerald-200 text-emerald-900";
  const valueColor = tone === "blue" ? "text-blue-700" : "text-emerald-700";
  return (
    <div className={`border rounded-xl px-4 py-3 ${styles}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <div className="text-[10px] font-bold tracking-widest uppercase opacity-80">{label}</div>
            <div className="text-[10px] opacity-60">{subLabel}</div>
          </div>
        </div>
        <div className={`font-mono font-bold text-3xl ${valueColor}`} data-testid={`${testId}-value`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, testId, type = "text", placeholder = "" }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        type={type}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 min-h-[44px] font-mono"
        placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );
}
